import { useState, useRef, useEffect, useCallback , useMemo } from "react";
import { createPortal } from "react-dom";
import TypewriterText from "./components/TypewriterText";
import DeskPanel from "./components/DeskPanel";
import WorldGuide from "./components/WorldGuide";
import WhiteboardPanel from "./components/WhiteboardPanel";
import { SpatialProvider, useSpatial } from "./components/SpatialContext";
import { useAudio } from "./components/AudioContext";
import BrainDumpModal from "./components/BrainDumpModal";
import MapView from "./components/MapView";
import FloatingTimer, { TYPE_COLORS } from "./components/FloatingTimer";
import MyDayPanel from "./components/MyDayPanel";
import CalendarPanel from "./components/CalendarPanel";
import BulletinPanel from "./components/BulletinPanel";
import UnityMessagePanel from "./components/UnityMessagePanel";
import UnityInspectionBar from "./components/unity/UnityInspectionBar";
import HotspotNavArrows from "./components/unity/HotspotNavArrows";
import {
  setUnityHotspotClickHandler,
  setUnityBuildingEnteredHandler,
  setUnityRoomEnteredHandler,
  setUnityHotspotClosedHandler,
  setUnityBuildingExitedHandler,
  setUnityDoorClickedHandler,
  setUnityNavigationHotspotHandler,
  setUnityInBuildingSession,
} from "./lib/unityEvents";
import {
  findBuildingRecord,
  resolveInspectionBuilding,
  locationFromBuildingId,
  locationFromRoomId,
  isDeskHotspotSurface,
  isNavigationHotspotRef,
  isUnityPlaceholderBuildingId,
  lastRoomIdFromSession,
  roomNameFromLocation,
  stripHavenFromDistricts,
} from "./lib/worldJson";
import { blocksForDate, toDateKey } from "./lib/plannerItems";
import { getUnityBridgeCommands, goToUnityRoom } from "./lib/unityCommandBridge";
import { uploadWhiteboardSnapshot, deleteRoomFile } from "./lib/roomFileStorage";
import { BookshelfProvider } from "./components/BookshelfContext";
import BookshelfPanel from "./components/BookshelfPanel";
import CouchPanel, { CouchPiP } from "./components/CouchPanel";
import FidgetWidget from "./components/FidgetWidget";
import PaintingPanel from "./components/PaintingPanel";
import HavenPanel from "./components/HavenPanel";
import HotspotPopup from "./components/HotspotPopup";
import AudioSettingsPanel  from "./components/AudioSettingsPanel";
import { hotspotIdFromUnityRef, havenPopupIdFromBuilding, havenPopupIdFromRef, resolveHavenPopupFromUnityPayload, isHavenSpacePopup } from "./lib/hotspotPopupContent";
import { planHotspotOpen, planHotspotClose } from "./lib/hotspotPopupFlow";
import { deskDocIdForBook } from "./lib/bookshelf";
import MagicLinkGate from "./components/auth/MagicLinkGate";
import { ACCESS_PATH, isAccessPath } from "./lib/authRoutes";
import { ClaudeService } from "./services/claudeService";
import { parseBrainDump } from "./services/brainDumpService";
import { settleStaleDeskReceipts } from "./lib/deskWrites";
import { useDeskActions } from "./hooks/useDeskActions";
import useUnitySession from "./hooks/useUnitySession";
import useBulletinBoardImages from "./hooks/useBulletinBoardImages";
import { AUDIO_DEFAULT_STATE } from "./lib/audioChannelIcons";
import supabase from "./services/supabaseClient";
import {
  capState, carryMessages, continuedTitle, createLumenSessionSync, generateLumenSessionTitle, listBuildingSessions, listLumenSessions,
  listSessionLinks, mergeSessionRows, relatedIdsFor, setRelatedSessions, upsertSessionRow,
} from "./services/lumenSessionService";
import LumenLeftOffPopup from "./components/lumen/LumenLeftOffPopup";
import { describeProject } from "./lib/projectOverview";
import useWhiteboardContents from "./hooks/useWhiteboardContents";
import {
  dockGeometry, loadLumenSplit, saveLumenSplit, clampLumenFrac,
  HOTSPOT_LABELS, HOTSPOT_PROMPT_CHIPS, DEFAULT_LUMEN_FRAC, DOCK_COLLAPSED_H, COLLAPSE_SNAP, SEAM_H, FREE_DOCK_KEY,
} from "./lib/lumenDock";
import { LumenCollapseIconSmall}  from "./lib/Icons/LumenIcon.js";
import LegalAgreementModal from "./lib/Modal/LegalAgreementModal.js";
import DialogHost from "./components/DialogHost";
import { alertDialog, confirmDialog } from "./lib/dialog";
import { AudioProvider } from "./components/AudioContext.js";
import FocusModeChat from "./components/lumen/FocusModeChat";
import LumenCard from "./components/lumen/LumenCard";

// const API_KEY = process.env.REACT_APP_CLAUDE_API_KEY;
// const API_HEADERS = {
//   "Content-Type": "application/json",
//   "x-api-key": API_KEY,
//   "anthropic-version": "2023-06-01",
//   "anthropic-dangerous-direct-browser-access": "true",
// };

// ── Lumen System Prompt v6 ───────────────────────────────────────
// Source of truth: lumen_system_prompt_v6.txt
// Research-backed coaching framework. Stripped tone rules. Spatial-first.
// Three coaching modes: momentum, Socratic, psychoeducation.

// ── Block Countdown Timer (shared state, used in card + minimized footer) ──
function useBlockCountdown(block) {
  const [secsLeft, setSecsLeft] = useState(block ? block.duration * 60 : 0);
  const [paused,   setPaused]   = useState(false);

  useEffect(() => {
    if (!block) return;
    setSecsLeft(block.duration * 60);
    setPaused(false);
  }, [block?.id]);

  useEffect(() => {
    if (!block || paused) return;
    const t = setInterval(() => setSecsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [block?.id, paused]);

  const fmtTime = s => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return { secsLeft, paused, setPaused, fmtTime };
}

// ── Mini pie SVG for block countdown ────────────────────────────
function BlockPie({ frac, color, size = 28 }) {
  const cx = size/2, cy = size/2, r = size/2 - 2, discR = r * 0.58;
  const a = frac * 2 * Math.PI;
  const path = frac >= 0.9999 ? `M ${cx} ${cy} m 0 -${r} a ${r} ${r} 0 1 1 -0.001 0 Z`
    : frac <= 0 ? `M ${cx} ${cy} Z`
    : `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${a > Math.PI ? 1 : 0} 1 ${cx + r*Math.sin(a)} ${cy - r*Math.cos(a)} Z`;
  return (
    <svg width={size} height={size} style={{ display: "block", flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill={`${color}18`} />
      <path d={path} fill={color} opacity={0.75} />
      <circle cx={cx} cy={cy} r={discR} fill="white" />
    </svg>
  );
}

// ── Block Session Footer (minimized view only) ──────────────────
function BlockSessionFooter({ block, secsLeft, paused, setPaused, fmtTime, onDeactivate }) {
  const c = TYPE_COLORS[block.type] || TYPE_COLORS.task;
  const frac = secsLeft / (block.duration * 60);

  return (
    <div style={{ padding: "6px 12px", borderTop: `1px solid ${c.border}22`, background: c.bg,
      display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
        <BlockPie frac={frac} color={c.border} size={26} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: c.border, fontFamily: "'DM Sans',sans-serif",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 }}>
            {block.label}
          </div>
          <div style={{ fontSize: 9, color: "#94A3B8", fontFamily: "'DM Sans',sans-serif" }}>
            {fmtTime(secsLeft)} left{paused && <span style={{ color: "#EF4444", fontWeight: 700 }}> · PAUSED</span>}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <button onClick={() => setPaused(p => !p)}
          style={{ padding: "2px 8px", borderRadius: 6, border: `1px solid ${c.border}44`,
            background: "white", color: c.border, fontSize: 9, fontWeight: 700,
            cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
          {paused ? "▶" : "⏸"}
        </button>
        <button onClick={onDeactivate}
          style={{ padding: "2px 6px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.08)",
            background: "white", color: "#94A3B8", fontSize: 9, cursor: "pointer",
            fontFamily: "'DM Sans',sans-serif" }}>
          ✕
        </button>
      </div>
    </div>
  );
}

const WHITEBOARD_FRAME = {
  widthPerCanvasHeight:  1.48,
  heightPerCanvasHeight: 0.85,
  centreLiftPerCanvasHeight: 0.029,
};

function unityCanvasArea() {
  const canvas = document.querySelector("canvas");
  const rect = canvas?.getBoundingClientRect();
  if (rect?.width && rect?.height) return rect;
  return { top: 60, left: 0, width: window.innerWidth, height: window.innerHeight - 60 };
}

function whiteboardFrameBounds() {
  const area = unityCanvasArea();
  const boardW = WHITEBOARD_FRAME.widthPerCanvasHeight * area.height;
  const boardH = WHITEBOARD_FRAME.heightPerCanvasHeight * area.height;
  const centreX = area.left + area.width / 2;
  const centreY = area.top + area.height / 2
    - WHITEBOARD_FRAME.centreLiftPerCanvasHeight * area.height;

  return {
    top:    Math.max(0, Math.round(centreY - boardH / 2)),
    left:   Math.max(0, Math.round(centreX - boardW / 2)),
    right:  Math.max(0, Math.round(window.innerWidth  - (centreX + boardW / 2))),
    bottom: Math.max(0, Math.round(window.innerHeight - (centreY + boardH / 2))),
  };
}

// ── Right Stack (My Day + Lumen, independent open/close, drag handle) ──
function RightStack({ roomName, buildingName, islandName, floorName, activeSurface, whiteboardContents, projectOverview, surfaceState, onSurfaceUpdate, onDeskAction, activeBlockId, onActivateBlock, blocks = [], myDayOpen, setMyDayOpen, lumenOpen, setLumenOpen, lumenMaximized, onLumenMaximize, onBrainDump, currentMode, userProfile, logEvent, hasWorld, previousSession, currentRoomActivity, justBuiltWorld, onJustBuiltWorldDismiss, onNavigateToLastRoom, onOpenMap, onOpenDesk, hideMyDay = false, lumenMessages, setLumenMessages, lumenExchangeCount, setLumenExchangeCount, lumenOnStartFresh, onParseWorld, onSaveWorld, world,isUserTnCNotAccepted, lumenDrafting = false, lumenWelcomeBack = false, lumenLastWorkedOn = null, onLumenWelcomeBackDismiss, lumenVisitedRoomThisSession = false }) {
  const viewportH = window.innerHeight - 60;
  const MIN_CARD  = 44;
  const GAP       = 8;

  const activeBlock = activeBlockId ? blocks.find(b => b.id === activeBlockId) : null;
  const myDayH = viewportH - GAP;

  const countdown = useBlockCountdown(activeBlock);

  const PANEL_W = 320;
  const myDayVisible = !hideMyDay && myDayOpen;
  const stackVisible = myDayVisible;

  return (
    <div style={{ position: "fixed", top: 58, right: 0, bottom: 0, zIndex: 55, display: "flex", flexDirection: "row", alignItems: "flex-start", pointerEvents: "none" }}>

      {/* Tab strip - only shows tabs for closed panels */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "absolute", right: 0, top: "8%", zIndex: 56, pointerEvents: isUserTnCNotAccepted() ? "none" : "auto" }}>
        {!hideMyDay && !myDayOpen && (
          <div onClick={() => setMyDayOpen(true)}
            style={{ writingMode: "vertical-rl", cursor: "pointer", userSelect: "none", padding: "12px 7px", borderRadius: "8px 0 0 8px", background: "rgba(255,255,255,0.85)", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "-2px 2px 10px rgba(0,0,0,0.06)", fontSize: 10, fontWeight: 700, color: "#64748B", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.04em", borderLeft: "3px solid transparent", pointerEvents: isUserTnCNotAccepted() ? "none" : "auto" }}>
            📅 My Day
            {activeBlock && (
              <span style={{ display: "block", width: 6, height: 6, borderRadius: "50%", background: "#4DB8B5", margin: "4px auto 0", animation: "pulse 1.5s infinite" }} />
            )}
          </div>
        )}
      </div>

      {/* Sliding panel container */}
      <div style={{ width: PANEL_W, height: "100%", display: "flex", flexDirection: "column", transform: stackVisible ? "translateX(0)" : `translateX(${PANEL_W}px)`, transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", pointerEvents: isUserTnCNotAccepted() ? "none" : "auto" }}>

        {/* My Day */}
        {!hideMyDay && (
        <div style={{ position: "relative", display: "flex", flexDirection: "column", background: "rgba(255,255,255,0.97)", backdropFilter: "blur(16px)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "16px 0 0 0", overflow: "hidden", boxShadow: "-4px 4px 24px rgba(0,0,0,0.06)", height: myDayOpen ? myDayH : 0, opacity: myDayOpen ? 1 : 0, flexShrink: 0, transition: "height 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.2s" }}>
          {myDayOpen && (
            <div onClick={() => setMyDayOpen(false)} title="Close My Day"
              style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "6%", cursor: "pointer", zIndex: 2, background: "transparent", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.03)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"} />
          )}
          <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "white", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", fontFamily: "'DM Sans',sans-serif" }}>📅 My Day</span>
              {activeBlock && (
                <span style={{ fontSize: 10, color: TYPE_COLORS[activeBlock.type]?.border || "#4DB8B5", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                  · {activeBlock.label} <span style={{ color: "#94A3B8", fontWeight: 400 }}>{countdown.fmtTime(countdown.secsLeft)} left</span>
                </span>
              )}
            </div>
            <button onClick={() => setMyDayOpen(false)} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }}><LumenCollapseIconSmall /></button>
          </div>

          {myDayOpen && (
            <MyDayPanel activeBlockId={activeBlockId} onActivateBlock={onActivateBlock} countdown={countdown} />
          )}
        </div>
        )}

      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>
    </div>
  );
}

function DockedTimerPill({ pomRun, setPomRun, pomSecs, phase, onDetach, onExpand }) {
  const FOCUS_COLOR = "#16A34A";
  const REST_COLOR  = "#2563EB";
  const fmt = s => `${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6,
      padding: "4px 8px 4px 12px", borderRadius: 28,
      background: pomRun
        ? (phase === "work" ? FOCUS_COLOR : REST_COLOR)
        : "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.15)",
      transition: "background 0.4s",
      fontFamily: "'DM Sans',sans-serif" }}>

      {/* Dot */}
      <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
        background: pomRun ? "rgba(255,255,255,0.9)" : "#CBD5E1" }} />

      {/* Time */}
      <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700,
        color: pomRun ? "white" : "rgba(255,255,255,0.8)", letterSpacing: "0.01em" }}>
        {fmt(pomSecs)}
      </div>

      {/* Phase label - only when running */}
      {pomRun && (
        <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.8)",
          textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {phase === "work" ? "Focus" : "Break"}
        </div>
      )}

      {/* Play/Pause */}
      <button onClick={() => setPomRun(r => !r)}
        style={{ width: 22, height: 22, borderRadius: "50%", border: "none", cursor: "pointer",
          background: pomRun ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.15)",
          color: "white", fontSize: 9,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
        {pomRun ? "⏸" : "▶"}
      </button>

      {/* Expand ▼ */}
      <button onClick={onExpand}
        style={{ background: "none", border: "none",
          color: pomRun ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.3)",
          fontSize: 9, cursor: "pointer", padding: "0 1px", lineHeight: 1 }}>▼</button>

      {/* Detach ⤢ */}
      <button onClick={onDetach}
        style={{ background: "none", border: "none",
          color: pomRun ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.3)",
          fontSize: 11, cursor: "pointer", padding: "0 1px", lineHeight: 1 }}>⤢</button>
    </div>
  );
}

// ── Wall Painting ───────────────────────────────────────────────
function WallPainting({ themeId, theme, onOpen }) {
  const paintings = {
    deep_focus: (
      <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="160" fill="#1A1A2E"/>
        <circle cx="100" cy="80" r="52" fill="none" stroke="#C8A04022" strokeWidth="28"/>
        <circle cx="100" cy="80" r="28" fill="#C8A04033"/>
        <circle cx="100" cy="80" r="12" fill="#C8A040BB"/>
        <circle cx="100" cy="80" r="4"  fill="#FFB060"/>
        <line x1="100" y1="10" x2="100" y2="38"  stroke="#C8A04044" strokeWidth="1"/>
        <line x1="100" y1="122" x2="100" y2="150" stroke="#C8A04044" strokeWidth="1"/>
        <line x1="10"  y1="80"  x2="38"  y2="80"  stroke="#C8A04044" strokeWidth="1"/>
        <line x1="162" y1="80"  x2="190" y2="80"  stroke="#C8A04044" strokeWidth="1"/>
      </svg>
    ),
    golden_hour: (
      <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="160" fill="#F5EDD8"/>
        <ellipse cx="100" cy="110" rx="80" ry="30" fill="#A8784A33"/>
        <ellipse cx="70"  cy="75"  rx="38" ry="44" fill="#C4956A55" transform="rotate(-12 70 75)"/>
        <ellipse cx="130" cy="68"  rx="30" ry="50" fill="#D4834A44" transform="rotate(8 130 68)"/>
        <ellipse cx="100" cy="60"  rx="22" ry="28" fill="#C8A04066"/>
        <circle  cx="100" cy="38"  r="14"           fill="#FFD06099"/>
        <ellipse cx="55"  cy="95"  rx="18" ry="10"  fill="#8B451366" transform="rotate(-20 55 95)"/>
        <ellipse cx="148" cy="100" rx="16" ry="8"   fill="#8B451344" transform="rotate(15 148 100)"/>
      </svg>
    ),
    creative_flow: (
      <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="160" fill="#F0EEEA"/>
        <rect x="20"  y="20"  width="80" height="70" fill="#E8484877" transform="rotate(-4 60 55)"/>
        <rect x="100" y="15"  width="70" height="55" fill="#F5A02077"  transform="rotate(6 135 42)"/>
        <rect x="30"  y="85"  width="60" height="55" fill="#4848E877"  transform="rotate(3 60 112)"/>
        <rect x="110" y="80"  width="75" height="60" fill="#E8487877"  transform="rotate(-5 147 110)"/>
        <rect x="75"  y="55"  width="55" height="55" fill="#F0EEEA" opacity="0.7"/>
        <circle cx="102" cy="82" r="18" fill="#E84878CC"/>
        <circle cx="102" cy="82" r="7"  fill="#F0EEEA"/>
      </svg>
    ),
    calm: (
      <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="160" fill="#B8C9A8"/>
        <ellipse cx="100" cy="140" rx="70" ry="25" fill="#6B8B6A55"/>
        <path d="M100 130 Q85 100 90 70 Q95 45 100 30 Q105 45 110 70 Q115 100 100 130Z" fill="#2A5A2A88"/>
        <path d="M100 110 Q72 95 60 75 Q55 58 68 52 Q82 48 100 65 Q118 48 132 52 Q145 58 140 75 Q128 95 100 110Z" fill="#7AB84077"/>
        <ellipse cx="60"  cy="115" rx="22" ry="12" fill="#2A5A2A55" transform="rotate(-25 60 115)"/>
        <ellipse cx="145" cy="108" rx="20" ry="10" fill="#2A5A2A44" transform="rotate(20 145 108)"/>
        <circle  cx="100" cy="30"  r="8"            fill="#C8E870AA"/>
        <path d="M40 60 Q50 45 60 60" stroke="#2A5A2A66" strokeWidth="2" fill="none"/>
        <path d="M145 70 Q155 55 165 68" stroke="#2A5A2A55" strokeWidth="2" fill="none"/>
      </svg>
    ),
    midnight: (
      <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="160" fill="#111118"/>
        <polygon points="100,18 118,65 168,65 128,94 142,142 100,112 58,142 72,94 32,65 82,65" fill="none" stroke="#4DB8B522" strokeWidth="1.5"/>
        <polygon points="100,35 113,72 152,72 121,94 132,130 100,108 68,130 79,94 48,72 87,72"  fill="none" stroke="#4DB8B544" strokeWidth="1"/>
        <polygon points="100,52 110,80 140,80 116,96 126,124 100,106 74,124 84,96 60,80 90,80"  fill="#4DB8B511"/>
        <circle cx="100" cy="90" r="16" fill="#4DB8B533"/>
        <circle cx="100" cy="90" r="7"  fill="#4DB8B5BB"/>
        <circle cx="100" cy="90" r="2"  fill="#40F0C0"/>
        {[0,1,2,3,4,5,6,7].map(i => {
          const a = (i * 45 - 90) * Math.PI / 180;
          return <line key={i} x1={100 + Math.cos(a)*20} y1={90 + Math.sin(a)*20} x2={100 + Math.cos(a)*32} y2={90 + Math.sin(a)*32} stroke="#4DB8B566" strokeWidth="1"/>;
        })}
      </svg>
    ),
    fresh_start: (
      <svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="160" fill="#F8F8F6"/>
        <line x1="30"  y1="80"  x2="170" y2="80"  stroke="#E8E4DC" strokeWidth="1"/>
        <line x1="100" y1="20"  x2="100" y2="140" stroke="#E8E4DC" strokeWidth="1"/>
        <rect x="55" y="42" width="90" height="76" rx="2" fill="none" stroke="#C0B0A0" strokeWidth="1.5"/>
        <rect x="62" y="50" width="76" height="60" rx="1" fill="none" stroke="#E8E4DC" strokeWidth="1"/>
        <circle cx="100" cy="80" r="22" fill="none" stroke="#A09080" strokeWidth="1.5"/>
        <circle cx="100" cy="80" r="10" fill="none" stroke="#C0DCFF"  strokeWidth="2"/>
        <circle cx="100" cy="80" r="3"  fill="#1A1A1A"/>
        <line x1="100" y1="58"  x2="100" y2="46"  stroke="#A09080" strokeWidth="1"/>
        <line x1="100" y1="102" x2="100" y2="114" stroke="#A09080" strokeWidth="1"/>
        <line x1="78"  y1="80"  x2="66"  y2="80"  stroke="#A09080" strokeWidth="1"/>
        <line x1="122" y1="80"  x2="134" y2="80"  stroke="#A09080" strokeWidth="1"/>
      </svg>
    ),
  };

  return (
    <div
      onClick={onOpen}
      title="Change painting"
      style={{
        position: "absolute",
        top: "18%",
        left: "50%",
        transform: "translateX(-50%)",
        width: 160,
        zIndex: 1,
        cursor: "pointer",
        filter: "drop-shadow(0 4px 18px rgba(0,0,0,0.28))",
        transition: "transform 0.3s ease, filter 0.3s ease",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateX(-50%) scale(1.03)"; e.currentTarget.style.filter = "drop-shadow(0 8px 28px rgba(0,0,0,0.38))"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateX(-50%)"; e.currentTarget.style.filter = "drop-shadow(0 4px 18px rgba(0,0,0,0.28))"; }}
    >
      {/* Frame */}
      <div style={{
        border: `6px solid ${theme.dark ? "#C8A040" : "#A09070"}`,
        borderRadius: 4,
        boxShadow: `inset 0 0 0 2px ${theme.dark ? "rgba(200,160,64,0.3)" : "rgba(160,144,112,0.3)"}`,
        overflow: "hidden",
        lineHeight: 0,
        transition: "border-color 0.6s ease",
      }}>
        <div style={{ transition: "opacity 0.4s ease" }}>
          {paintings[themeId] || paintings.golden_hour}
        </div>
      </div>
      {/* Hanging wire */}
      <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", width: 60, height: 14, overflow: "hidden" }}>
        <svg viewBox="0 0 60 14" style={{ width: "100%", height: "100%" }}>
          <path d="M0 0 Q30 14 60 0" fill="none" stroke={theme.dark ? "rgba(200,160,64,0.5)" : "rgba(120,100,70,0.4)"} strokeWidth="1.5"/>
        </svg>
      </div>
    </div>
  );
}

function buildRoomWelcome(world, location) {
  if (!location?.roomId || !world?.districts) return null;
  for (const d of world.districts) {
    for (const isl of d.islands || []) {
      for (const b of isl.buildings || []) {
        for (const f of b.floors || []) {
          const room = (f.rooms || []).find(r => r.id === location.roomId);
          if (room) {
            const label = /\broom$/i.test((room.name || "").trim()) ? room.name : `${room.name} room`;
            return `Welcome to the ${label}. What do you need to figure out here?`;
          }
        }
      }
    }
  }
  return null;
}

// ── Desk auto-populate helpers ──────────────────────────────────

function buildTemplateDeskDoc(roomName, buildingName) {
  const name = (roomName || '').toLowerCase();
  const contextNote = buildingName ? ` in ${buildingName}` : '';

  if (/pitch|investor|deck|funding|series|raise|capital/.test(name)) {
    return {
      title: `${roomName}: Pitch Deck`,
      chatMsg: `There's a pitch deck outline on your desk${contextNote}. What's the part you feel clearest on right now?`,
      content: `<h2>Pitch Deck</h2><h3>The Problem</h3><p>What pain are you solving, and who feels it most?</p><h3>The Solution</h3><p>What you built and why it works.</p><h3>Why Now</h3><p>What's changed that makes this the right moment.</p><h3>Traction</h3><p>What's working so far.</p><h3>The Ask</h3><p>What you need and what you'll do with it.</p>`,
    };
  }
  if (/market|brand|launch|campaign|growth|gtm|go.to.market/.test(name)) {
    return {
      title: `${roomName}: Marketing Plan`,
      chatMsg: `Marketing plan outline is on your desk. What's the channel or audience you're clearest on right now?`,
      content: `<h2>Marketing Plan</h2><h3>Who We're Reaching</h3><p>Primary audience and what they care about.</p><h3>Core Message</h3><p>The one thing they should walk away believing.</p><h3>Channels</h3><ul><li>Channel 1</li><li>Channel 2</li><li>Channel 3</li></ul><h3>Launch Sequence</h3><p>What happens first, and in what order.</p><h3>How We'll Know It's Working</h3><p>The numbers that matter.</p>`,
    };
  }
  if (/research|discover|user|interview|insight|usability/.test(name)) {
    return {
      title: `${roomName}: Research Notes`,
      chatMsg: `Research notes are on your desk. What's the main question you're trying to answer?`,
      content: `<h2>Research Notes</h2><h3>What We're Trying to Learn</h3><p>The core question driving this research.</p><h3>Who We're Talking To</h3><p>Participant profile and how we're finding them.</p><h3>Key Themes</h3><ul><li>Theme 1</li><li>Theme 2</li><li>Theme 3</li></ul><h3>Standout Quotes</h3><blockquote>Add quotes here as you collect them.</blockquote><h3>What This Changes</h3><p>Decisions or directions this research affects.</p>`,
    };
  }
  if (/sprint|project|roadmap|plan|milestone|timeline|scope/.test(name)) {
    return {
      title: `${roomName}: Project Plan`,
      chatMsg: `Project plan is on your desk. What's the one thing that needs to be true by the end of this sprint?`,
      content: `<h2>Project Plan</h2><h3>What Done Looks Like</h3><p>The clearest version of success for this project.</p><h3>Key Milestones</h3><ol><li>Milestone 1</li><li>Milestone 2</li><li>Milestone 3</li></ol><h3>What Could Block Us</h3><p>Risks worth naming now so they don't surface later.</p><h3>Who Needs to Know What</h3><p>Stakeholders and what they care about.</p>`,
    };
  }
  if (/content|blog|copy|writing|article|post|editorial/.test(name)) {
    return {
      title: `${roomName}: Content Brief`,
      chatMsg: `Content brief is on your desk. What's the feeling you want someone to have after reading this?`,
      content: `<h2>Content Brief</h2><h3>The Point</h3><p>One sentence: what this piece is about and why it matters.</p><h3>Who It's For</h3><p>The person reading this and what they already believe.</p><h3>The Arc</h3><ol><li>Where we start</li><li>What shifts</li><li>Where we land</li></ol><h3>What Makes It Worth Reading</h3><p>The specific thing only this piece can say.</p>`,
    };
  }
  return {
    title: `${roomName || 'Work'}: Starter Doc`,
    chatMsg: `There's a starting point on your desk${contextNote}. What's the most important thing to figure out first?`,
    content: `<h2>${roomName || 'Getting Started'}</h2><h3>What We're Working On</h3><p>The core goal for this room.</p><h3>Where We Are Now</h3><p>Current state and what's already been decided.</p><h3>Next Moves</h3><ul><li>First step</li><li>Second step</li><li>Third step</li></ul><h3>Open Questions</h3><p>Things to resolve before moving forward.</p>`,
  };
}

function buildDeskDraftSystemPrompt(roomName, buildingName, brainDumpItems) {
  const itemLines = brainDumpItems
    .map(item => `[${item.surface || 'note'}] ${(item.content || '').substring(0, 200)}`)
    .filter(Boolean)
    .join('\n');
  return `You are Lumen, drafting a starter desk document for a room in NeuroQuest.

Room: ${roomName}${buildingName ? ` (part of ${buildingName})` : ''}

Content from brain dump already captured for this room:
${itemLines}

Your task: Draft a clear, useful HTML starter document that organizes this content into a working document the user can build on.

Rules:
- Use only: <h2>, <h3>, <p>, <strong>, <em>, <ul>, <li>, <ol>, <blockquote>, <hr>. Never <h1>.
- Write a warm, brief chat_response (2-3 sentences) that ends with one low-pressure question about where to start. No status report.
- No mechanism or architecture language anywhere. Benefit and feeling language only.
- chat_response must never be empty.

Respond with valid JSON only:
{
  "chat_response": "...",
  "surface_updates": [{"surface": "desk", "type": "document", "content": "<h2>...</h2>...", "title": "..."}],
  "desk_action": "new_doc"
}`;
}

async function autoDraftDeskDocument({ roomId, roomName, buildingName, brainDumpItems, userProfile, addSurfaceItem, setLumenMessages }) {
  try {
    const result = await ClaudeService.sendStructuredMessage({
      model: 'claude-haiku-4-5-20251001',
      maxTokens: 1500,
      system: buildDeskDraftSystemPrompt(roomName, buildingName, brainDumpItems),
      messages: [{ role: 'user', content: 'Draft the starter document for this room.' }],
    });

    if (!result.success) throw new Error(result.error || 'Draft failed');

    const parsed = result.data || {};
    const deskUpdate = (parsed.surface_updates || []).find(u => u.surface === 'desk');
    if (deskUpdate) {
      addSurfaceItem({ ...deskUpdate, room_id: roomId, forceNew: true });
    }
    const chatMsg = parsed.chat_response ||
      `There's a starting point on your desk for ${roomName}. Where do you want to dig in first?`;
    setLumenMessages(prev => [...prev, { role: 'assistant', content: chatMsg, isWelcome: false }]);
  } catch {
    // API failed — fall back to keyword template so the desk is never blank
    const { title, content, chatMsg } = buildTemplateDeskDoc(roomName, buildingName);
    addSurfaceItem({ surface: 'desk', type: 'document', content, title, room_id: roomId, forceNew: true });
    setLumenMessages(prev => [...prev, { role: 'assistant', content: chatMsg, isWelcome: false }]);
  }
}

// ── Inner App ───────────────────────────────────────────────────
const UNITY_TAP_SLOP_PX = 8;

function InnerApp() {
  const {
    world, currentLocation, currentLocationRef, mapOpen, setMapOpen, getCurrentRoom, surfaceState, addSurfaceItem,
    roomSurfaceItems, persistedSurfaceItems, persistedSurfaceItemsRef, setWhiteboardImage, setBulletinImage,
    findSurfaceItemById, hydrateDeskFromSurfaceItem, enterRoomForSurfaceItem,
    updateDeskDocument, deleteDeskDocument, resetDeskAutosaveUi, flushWorldJson,
    registerDeskFlush,
    worldSaving, worldLastSavedAt, worldAutosavePending, worldSaveError,
    worldReady, needsBrainDump, worldLoadFailed,
    user, appUser, userProfile, setUserProfile, logEvent, signOut, enterRoom, previousSession, currentRoomActivity, roomSummaryVersion,
    resetToNewUser, justBuiltWorld, setJustBuiltWorld, updateMapPosition, loadParsedWorld,isUserTnCNotAccepted,
    profileChecked
  } = useSpatial();

  const { setAllMuted } = useAudio();

  const handleParseWorld = useCallback(async (chatHistory, _existingWorld) => {
    // Always read from worldRef — avoids stale prop right after a save
    const currentWorld = worldRef.current;
    return await parseBrainDump(
      chatHistory,
      currentWorld?.districts?.length ? currentWorld : null
    );
  }, []); // worldRef is a stable ref object, no dep needed

  const handleSaveWorld = useCallback(async (parsedWorld) => {
    try {
      return await loadParsedWorld(parsedWorld);
    } catch (e) {
      return { ok: false, error: e.message || "Failed to build your world. Try Brain Dump instead." };
    }
  }, [loadParsedWorld]);
  const [unityLoaded, setUnityLoaded] = useState(false);
  const [unityPlacementReady, setUnityPlacementReady] = useState(false);
  const [lumenOpenPending, setLumenOpenPending] = useState(false);

  const worldRevealed = unityLoaded && unityPlacementReady;
  const worldRevealedRef = useRef(false);
  worldRevealedRef.current = worldRevealed;
  const requestLumenOpen = () => {
    if (worldRevealedRef.current) setLumenOpen(true);
    else setLumenOpenPending(true);
  };
  useEffect(() => {
    if (!worldRevealed || !lumenOpenPending) return;
    setLumenOpenPending(false);
    setLumenOpen(true);
  }, [worldRevealed, lumenOpenPending]); // eslint-disable-line react-hooks/exhaustive-deps

  /** True once the user has been inside a room during this page session. */
  const [visitedRoomThisSession, setVisitedRoomThisSession] = useState(false);
  const [activeHotspot, setActiveHotspot]   = useState(null);
  const activeHotspotRef = useRef(activeHotspot);
  activeHotspotRef.current = activeHotspot;
  const [whiteboardContents, setWhiteboardContents] = useWhiteboardContents(user?.id, currentLocation.roomId);
  const [hotspotChips, setHotspotChips] = useState(null);
  const [buildingActivity, setBuildingActivity] = useState([]);
  const [worldGuideOpen, setWorldGuideOpen] = useState(false);
  const [brainDumpOpen, setBrainDumpOpen]   = useState(false);
  const brainDumpOnboardingShownRef = useRef(false);
  /** Keep Unity mounted after first load — avoids reload loop on auth token refresh. */
  const unityKeepMountedRef = useRef(false);
  const [deskFocusTitle, setDeskFocusTitle] = useState(null);
  const [activeBlockId, setActiveBlockId]   = useState(null);
  const dayBlocks = useMemo(
    () => blocksForDate(persistedSurfaceItems, toDateKey()).map((b) => ({
      id: b.id, label: b.title, start: b.start_time, duration: b.duration_minutes, type: b.type,
    })),
    [persistedSurfaceItems]
  );
  const [timerDocked, setTimerDocked]       = useState(true);
  const [timerExpanded, setTimerExpanded]   = useState(false);
  const [roomGreeting, setRoomGreeting]     = useState(null); // { roomId, itemCount }
  const [unityInspection, setUnityInspection] = useState(null); // { buildingId, buildingName?, mode: 'inspection' | 'firstperson' }
  const unityInspectionRef = useRef(unityInspection);
  unityInspectionRef.current = unityInspection;
  useEffect(() => {
    setUnityInBuildingSession(false);
  }, []);
  /** Block full map-exit while door/back returns to inspection (Unity may still be on aerial). */
  const suppressFullBuildingExitUntilRef = useRef(0);
  const returnToInspectionTimersRef = useRef([]);
  /** True after RoomEntered until door/back returns to inspection (Unity may not re-send RoomEntered). */
  const inUnityRoomRef = useRef(false);
  /** Prevent duplicate state sync on door exit. */
  const doorExitHandledUntilRef = useRef(0);
  /** User chose Exit to map — next BuildingExited is intentional, not a room door. */
  const explicitMapExitRef = useRef(false);
  /** Set by the Unity handler effect; syncs React to where SessionRestored put the user. */
  const sessionRestoredSyncRef = useRef(null);
  /** Survives desk overlay / missed RoomEntered — restore building session on door exit. */
  const buildingSessionRef = useRef(null);
  /** True between door click and BuildingExited (Unity may skip RoomEntered on re-entry). */
  const doorClickArmedRef = useRef(false);
  /** Pointer-down point on the Unity surface — tells a tap apart from a camera drag. */
  const unityTapStartRef = useRef(null);
  /** Pending "did that tap drop us back to aerial?" check. */
  const aerialProbeTimerRef = useRef(null);
  /** Set by the Unity handler effect; runs the same cleanup as a BuildingExited event. */
  const inspectionTapProbeRef = useRef(null);
  const pendingUnityRoomIdRef = useRef(null);
  /** Set when BuildingExited fires — covers re-entry when RoomEntered was skipped. */
  const buildingExitedPendingRef = useRef(false);
  /** Last building visited — used to pick the *other* building when Unity sends placeholder id "building". */
  const lastVisitedBuildingIdRef = useRef(null);
  /** Ignore stale BuildingExited from the previous building right after entering a new one. */
  const ignoreBuildingExitUntilRef = useRef(0);
  /** Unity often fires BuildingEntered twice: real id then placeholder "building" — ignore the duplicate. */
  const lastConcreteBuildingEnteredAtRef = useRef(0);
  const lastConcreteBuildingIdRef = useRef(null);
  /** Unity can fire RoomEntered more than once for the same room in quick succession — ignore repeats. */
  const lastRoomEnteredIdRef = useRef(null);
  const lastRoomEnteredAtRef = useRef(0);
  const worldRef = useRef(world);
  worldRef.current = world;
  // currentLocationRef comes from useSpatial() — it is updated immediately inside
  // enterRoom() so hotspot clicks and other same-tick reads always see the latest roomId.
  const patchUnityInspection = (updater) => {
    setUnityInspection((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      unityInspectionRef.current = next;
      setUnityInBuildingSession(next != null);
      return next;
    });
  };

  const enterUnityRoomFirstPerson = async (location, { buildingName } = {}) => {
    if (!location?.roomId) return { ok: false, error: "missing roomId" };

    const buildingRecord =
      buildingName == null
        ? findBuildingRecord(worldRef.current.districts, location.buildingId, {
            roomId: location.roomId,
            preferLocationHint: true,
          })
        : null;

    const session = {
      buildingId: location.buildingId,
      buildingName: buildingName ?? buildingRecord?.building?.name ?? null,
      mode: "firstperson",
    };
    buildingSessionRef.current = session;
    suppressFullBuildingExitUntilRef.current = Date.now() + 4000;
    ignoreBuildingExitUntilRef.current = Date.now() + 3000;
    inUnityRoomRef.current = true;

    const unityResult = await goToUnityRoom(location.roomId);
    enterRoom(location);
    patchUnityInspection(session);
    return unityResult;
  };

  // Unity restores the saved position; React stores it and keeps the loader up until it lands.
  useUnitySession({
    userId: user?.id ?? null,
    remoteEntry: userProfile?.user_id === user?.id ? userProfile?.unity_session ?? null : null,
    profileReady: Boolean(profileChecked && user?.id && userProfile?.user_id === user.id),
    onRestored: (restored) => {
      sessionRestoredSyncRef.current?.(restored);
      setUnityPlacementReady(true);
    },
    prepareRestore: async (state) => {
      if (state?.mode !== "FirstPersonView" || !state.hotspotId || !state.roomId || !user?.id) return state;
      const { data, error } = await supabase
        .from("lumen_sessions")
        .select("id")
        .eq("user_id", user.id)
        .eq("room_id", state.roomId)
        .eq("kind", "room")
        .limit(1);
      if (error || !data?.length) return state;
      heldRestoreHotspotRef.current = { roomId: state.roomId, hotspotId: state.hotspotId, needsGo: true };
      console.info("[UnitySession] Holding hotspot until Pick up:", state.hotspotId);
      const { hotspotId, ...entranceState } = state;
      return entranceState;
    },
  });

  const unityDistricts = useMemo(() => stripHavenFromDistricts(world?.districts || []), [world?.districts]);
  useBulletinBoardImages({
    userId: user?.id ?? null,
    enabled: Boolean(worldReady && !needsBrainDump),
    roomId: currentLocation.roomId,
    districts: unityDistricts,
    surfaceItems: persistedSurfaceItems,
    setBulletinImage,
  });

  useEffect(() => {
    if (!currentLocation.roomId) return;
    setVisitedRoomThisSession(true);
  }, [currentLocation.roomId]);

  // Safety net if SessionRestored never arrives (e.g. an older Unity build).
  useEffect(() => {
    if (!unityLoaded) return;
    const t = setTimeout(() => setUnityPlacementReady(true), 10000);
    return () => clearTimeout(t);
  }, [unityLoaded]);

  const prevHotspotRoomRef = useRef(currentLocation.roomId);
  useEffect(() => {
    if (prevHotspotRoomRef.current === currentLocation.roomId) return;
    prevHotspotRoomRef.current = currentLocation.roomId;
    if (unityHotspotRef.current?.roomId !== currentLocation.roomId) unityHotspotRef.current = null;
    setActiveHotspot(null);
    closePendingDeskAction({ leavingRoom: true });
  }, [currentLocation.roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const restoreHotspotRef = useRef(null);
  const unityHotspotRef = useRef(null);
  const pendingRestoreHotspotRef = useRef(null);
  const heldRestoreHotspotRef = useRef(null);
  useEffect(() => {
    if (heldRestoreHotspotRef.current?.roomId !== currentLocation.roomId) heldRestoreHotspotRef.current = null;
    const pending = pendingRestoreHotspotRef.current;
    if (!pending || pending.roomId !== currentLocation.roomId) return undefined;
    pendingRestoreHotspotRef.current = null;
    if (lumenActiveRoomIdRef.current !== currentLocation.roomId) {
      heldRestoreHotspotRef.current = pending;
      return undefined;
    }
    const timer = setTimeout(() => restoreHotspotRef.current?.(pending.hotspotId), 0);
    return () => clearTimeout(timer);
  }, [currentLocation.roomId]);

  const worldIsBuilt = Boolean(worldReady && !needsBrainDump && world?.districts?.length > 0);
  useEffect(() => {
    if (!worldIsBuilt || currentLocation.roomId) return;
    if (!lastRoomIdFromSession(previousSession)) return;
    setLumenMaximized(false);
    requestLumenOpen();
  }, [worldIsBuilt, currentLocation.roomId, previousSession?.last_room_id, previousSession?.last_activity?.room_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentLocation.roomId && currentLocation.buildingId) return;

    const roomId = currentLocation.roomId || (user?.id ? '__haven__' : null);
    if (roomId && roomId === lumenActiveRoomIdRef.current) return;
    lumenActiveRoomIdRef.current = roomId;

    const runToken = ++lumenLoadRunRef.current;
    const releaseLoadLock = () => {
      if (runToken === lumenLoadRunRef.current) lumenLoadingRef.current = false;
    };

    lumenLoadingRef.current = true;
    clearTimeout(lumenPersistTimerRef.current);
    setLumenMessages([]);
    setLumenExchangeCount(0);
    setLumenDrafting(false);
    setLumenWelcomeBack(null);
    setLumenSessions([]);
    setLumenActiveSession(null);
    setLumenLinks([]);
    setLumenLeftOff(null);

    if (!roomId || !user?.id) {
      lumenSyncRef.current = null;
      releaseLoadLock();
      return;
    }

    console.warn('[Lumen] room entry — roomId for read/write:', roomId);

    const sync = createLumenSessionSync({
      userId: user.id,
      roomId,
      buildingId: currentLocation.buildingId || null,
      onSessionChange: (row) => {
        if (lumenSyncRef.current !== sync) return;
        setLumenActiveSession(row);
        if (row) setLumenSessions((prev) => upsertSessionRow(prev, row));
      },
    });
    lumenSyncRef.current = sync;

    listLumenSessions({ userId: user.id, roomId }).then(({ sessions, error }) => {
      if (lumenSyncRef.current !== sync) return;
      if (error) console.error('[Lumen] session list error:', error.message);
      else setLumenSessions((prev) => mergeSessionRows(sessions, prev));
    });

    const pendingOpen = pendingOpenSessionRef.current?.roomId === roomId ? pendingOpenSessionRef.current.sessionId : null;
    pendingOpenSessionRef.current = null;

    let cancelled = false;
    (pendingOpen ? sync.open(pendingOpen).then((result) => (result.error ? sync.load() : result)) : sync.load())
      .then(({ messages, error, session }) => {
        if (cancelled) return;
        const takeHeldHotspot = () => {
          const held = heldRestoreHotspotRef.current;
          if (held?.roomId !== roomId) return null;
          heldRestoreHotspotRef.current = null;
          return held;
        };
        const showsLeftOff = !error && messages.length > 0 && !pendingOpen && !!currentLocation.roomId;
        if (!showsLeftOff) {
          const held = takeHeldHotspot();
          if (held) {
            setTimeout(() => {
              if (restoreHotspotRef.current?.(held.hotspotId) && held.needsGo) getUnityBridgeCommands()?.goToHotspot?.(held.hotspotId);
            }, 0);
          }
        }
        console.log('[Lumen welcome] roomId:', roomId, '| error:', error?.message ?? null, '| messages:', messages?.length ?? 'null/undefined');
        if (error) {
          console.error('[Lumen] load error:', error.message);
          releaseLoadLock();
          return;
        }
        if (messages.length > 0) {
          // Mark every loaded message as restored so the typewriter doesn't replay on them.
          setLumenMessages(settleStaleDeskReceipts(messages).map(m => ({ ...m, isRestored: true })));
          // Restore exchange count so turn-2+ draft instruction fires on the right turn
          setLumenExchangeCount(messages.filter(m => m.role === 'user').length);
          autoTitleLumenSession(sync, messages);
          if (pendingOpen) {
            setLumenMaximized(false);
            requestLumenOpen();
          } else if (currentLocation.roomId) {
            setLumenOpen(false);
            setLumenLeftOff({
              roomId,
              sessionId: session?.id || null,
              surface: session?.last_surface || session?.surface || null,
              hotspotId: session?.last_hotspot_id || null,
              restoredHotspot: takeHeldHotspot(),
              title: session?.title || null,
            });
          } else {
            setLumenWelcomeBack(roomId);
            requestLumenOpen();
          }
        } else {
          // No prior history — auto-populate desk if this is a real room (not haven/sentinel)
          if (currentLocation.roomId) {
            const allRoomItems = persistedSurfaceItemsRef.current.filter(item => item.room_id === roomId);
            // Brain dump items: any surface item with content that isn't the desk itself
            const brainDumpItems = allRoomItems.filter(
              item => (item.content || '').trim() &&
                item.surface !== 'desk' &&
                item.surface !== 'table'
            );
            // Desk is "blank" if no item has meaningful content
            const hasDeskContent = allRoomItems.some(
              item => (item.surface === 'desk' || item.surface === 'table') &&
                (item.content || '').trim().length > 20
            );

            if (!hasDeskContent && brainDumpItems.length > 0) {
              // Has brain dump context — call Lumen to draft a contextual starter doc
              const roomCtx = getCurrentRoom();
              const roomName = roomCtx?.room?.name || currentLocation.roomId;
              setLumenMessages(prev => [...prev, {
                role: 'assistant',
                content: `Welcome to ${roomName}. Let me put together a starting point based on what you've shared.`,
                isWelcome: true,
              }]);
              setLumenMaximized(false);
              requestLumenOpen();
              setLumenDrafting(true);
              autoDraftDeskDocument({
                roomId: currentLocation.roomId,
                roomName,
                buildingName: roomCtx?.building?.name || '',
                brainDumpItems,
                userProfile,
                addSurfaceItem,
                setLumenMessages,
              }).finally(() => {
                if (!cancelled) setTimeout(async () => {
                  setLumenDrafting(false);
                  releaseLoadLock();
                  // The normal persist effect was blocked by lumenLoadingRef during the async
                  // draft call. Flush the conversation directly now that the lock is released.
                  const { error } = await sync.save(lumenMessagesRef.current);
                  if (error) console.error('[Lumen] persist error:', error.message);
                }, 300);
              });
              return; // autoDraft handles lumenLoadingRef release — skip the setTimeout below
            }

            if (!hasDeskContent) {
              // Blank desk, no brain dump context — apply keyword-matched template
              const roomCtx = getCurrentRoom();
              const roomLabel = roomCtx?.room?.name || '';
              const { title, content, chatMsg } = buildTemplateDeskDoc(
                roomLabel,
                roomCtx?.building?.name || ''
              );
              addSurfaceItem({ surface: 'desk', type: 'document', content, title, room_id: currentLocation.roomId, forceNew: true });
              // First visit always opens with "Welcome to <room>" before the desk hand-off.
              setLumenMessages([{ role: 'assistant', content: roomLabel ? `Welcome to ${roomLabel}. ${chatMsg}` : chatMsg, isWelcome: true }]);
              setLumenMaximized(false);
              requestLumenOpen();
            } else {
              // Desk already has content — just show the contextual welcome.
              const welcome = buildRoomWelcome(worldRef.current, currentLocationRef.current);
              if (welcome) {
                setLumenMessages([{ role: 'assistant', content: welcome, isWelcome: true }]);
                setLumenMaximized(false);
                requestLumenOpen();
              }
            }
          } else {
            // Haven or no room context — original welcome path
            const welcome = buildRoomWelcome(worldRef.current, currentLocationRef.current);
            if (!welcome && lastRoomIdFromSession(previousSessionRef.current)) {
              setLumenMaximized(false);
              requestLumenOpen();
            }
            if (welcome) {
              setLumenMessages([{ role: 'assistant', content: welcome, isWelcome: true }]);
              setLumenMaximized(false);
              requestLumenOpen();
            }
          }
        }
        // Release write block after React settles the state update
        setTimeout(releaseLoadLock, 200);
      });

    return () => { cancelled = true; };
  }, [currentLocation.roomId, currentLocation.buildingId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared guard: skip blank/near-blank placeholders before pushing into deskDocuments.
  // Both the auto-entry hydration effect and the user-initiated openDeskPanel path use this
  // so the threshold lives in one place.
  const hydrateDesk = (item) => {
    if ((item?.content || '').trim().length >= 20) hydrateDeskFromSurfaceItem(item);
  };

  // ── Room entry: hydrate desk docs from persistedSurfaceItems for this room ──
  useEffect(() => {
    if (!currentLocation.roomId) return;
    const items = roomSurfaceItems[currentLocation.roomId] || [];
    items
      .filter(item => isDeskHotspotSurface(item.surface))
      .forEach(hydrateDesk);
  }, [currentLocation.roomId, roomSurfaceItems]);

  // ── Unity bridge: hotspot, building inspection, room entry ──
  useEffect(() => {
    const findRecord = (id) => findBuildingRecord(worldRef.current.districts, id);

    const openHavenSpacePopup = (havenPopupId, { buildingId, hotspotId } = {}) => {
      if (!havenPopupId) return false;
      if (!hotspotPopupHandlersRef.current.requestHotspotPopup?.(havenPopupId)) return false;
      logEvent("haven_building_popup", {
        building_id: buildingId,
        hotspot_id: hotspotId,
        popup: havenPopupId,
      });
      return true;
    };

    const showHavenPopup = (hotspotId, rawPayload) => {
      const havenPopupId = resolveHavenPopupFromUnityPayload(hotspotId, rawPayload, findRecord);
      return openHavenSpacePopup(havenPopupId, { hotspotId, buildingId: hotspotId });
    };

    const handleHotspotClick = (hotspotId, rawPayload) => {
      if (showHavenPopup(hotspotId, rawPayload)) return;

      if (!hotspotId) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[Unity → React] HotspotClicked: empty hotspot id from Unity", rawPayload);
        }
        return;
      }

      if (isNavigationHotspotRef(hotspotId)) return;

      if (unityInspectionRef.current?.mode === "inspection") {
        inUnityRoomRef.current = true;
        patchUnityInspection((prev) => (prev ? { ...prev, mode: "firstperson" } : null));
      }

      // When Unity sends a bare surface name (e.g. "table", "desk") with no room context,
      // retry the lookup using the current room to build a compound key.
      let resolvedId = hotspotId;
      let item = findSurfaceItemById(hotspotId);
      if (!item && !hotspotId.includes(":") && !hotspotId.startsWith("surface_")) {
        console.log('[DEBUG fallback] currentLocationRef.current:', JSON.stringify(currentLocationRef.current), '| roomId:', currentLocationRef.current?.roomId);
        const roomId = currentLocationRef.current?.roomId;
        if (roomId) {
          resolvedId = `${roomId}:${hotspotId}`;
          item = findSurfaceItemById(resolvedId);
        }
      }

      const popupId = hotspotIdFromUnityRef(resolvedId, findSurfaceItemById);
      const clickedPanel = item && isDeskHotspotSurface(item.surface) ? "desk" : popupId;
      unityHotspotRef.current = hotspotId.includes(":") && clickedPanel
        ? { id: hotspotId, panel: clickedPanel, roomId: item?.room_id || currentLocationRef.current?.roomId || null }
        : null;
      if (popupId && hotspotPopupHandlersRef.current.requestHotspotPopup?.(popupId, { surfaceItem: item })) {
        if (item?.room_id) enterRoomForSurfaceItem(item);
        logEvent("hotspot_clicked", {
          surface_item_id: item?.id ?? hotspotId,
          room_id: item?.room_id,
          surface: item?.surface ?? popupId,
          popup: true,
        });
        return;
      }

      if (!item) {
        console.warn("[Unity → React] HotspotClicked: no surface_item for id", hotspotId);
        return;
      }
      if (!isDeskHotspotSurface(item.surface)) {
        return;
      }
      hotspotPopupHandlersRef.current.openDeskPanel?.(item);
    };

    const clearBuildingSessionForMap = () => {
      const leavingId =
        unityInspectionRef.current?.buildingId ?? buildingSessionRef.current?.buildingId;
      if (leavingId) lastVisitedBuildingIdRef.current = leavingId;
      lastConcreteBuildingEnteredAtRef.current = 0;
      lastConcreteBuildingIdRef.current = null;
      clearReturnToInspectionTimers();
      doorClickArmedRef.current = false;
      buildingExitedPendingRef.current = false;
      inUnityRoomRef.current = false;
      buildingSessionRef.current = null;
      suppressFullBuildingExitUntilRef.current = 0;
      patchUnityInspection(null);
      enterRoom(locationFromBuildingId(worldRef.current.districts, null, {}));
      setLumenOpen(false);
      setLumenMessages([]);
      setLumenDrafting(false);
      lumenActiveRoomIdRef.current = null;
      if (user?.id) {
        supabase.from('user_profile').update({ last_activity: null }).eq('user_id', user.id);
      }
    };

    const handleBuildingEntered = (buildingId, rawPayload) => {
      const districts = worldRef.current.districts;
      const parsed = rawPayload && typeof rawPayload === "object" ? rawPayload : null;
      const idCandidate =
        (typeof buildingId === "string" ? buildingId : null) ||
        parsed?.buildingID ||
        parsed?.buildingId ||
        parsed?.id ||
        null;

      const isPlaceholder = isUnityPlaceholderBuildingId(idCandidate);

      if (isPlaceholder) {
        const activeId =
          unityInspectionRef.current?.buildingId ?? buildingSessionRef.current?.buildingId;
        if (activeId && !isUnityPlaceholderBuildingId(activeId)) {
          return;
        }
        const sinceConcrete = Date.now() - lastConcreteBuildingEnteredAtRef.current;
        if (sinceConcrete < 2500 && lastConcreteBuildingIdRef.current) {
          return;
        }
      } else if (idCandidate) {
        lastConcreteBuildingEnteredAtRef.current = Date.now();
        lastConcreteBuildingIdRef.current = idCandidate;
      }

      clearReturnToInspectionTimers();
      doorClickArmedRef.current = false;
      buildingExitedPendingRef.current = false;
      inUnityRoomRef.current = false;
      ignoreBuildingExitUntilRef.current = Date.now() + 3000;

      const previousBuildingId = isPlaceholder ? lastVisitedBuildingIdRef.current : null;
      const record = findBuildingRecord(districts, idCandidate, {
        preferLocationHint: false,
        excludeBuildingId: previousBuildingId,
        preferHavenIsland: isPlaceholder,
      });
      const resolvedId = record?.building?.id ?? idCandidate;
      if (isPlaceholder && isUnityPlaceholderBuildingId(resolvedId)) {
        console.warn("[Unity → React] BuildingEntered: could not resolve placeholder building id");
        return;
      }
      if (resolvedId) lastVisitedBuildingIdRef.current = resolvedId;

      const havenPayload = {
        ...parsed,
        name: record?.building?.name,
        buildingName: record?.building?.name,
      };
      const havenPopupId =
        resolveHavenPopupFromUnityPayload(resolvedId, havenPayload, findRecord) ||
        havenPopupIdFromBuilding(record?.building) ||
        havenPopupIdFromRef(idCandidate) ||
        havenPopupIdFromRef(resolvedId);
      if (havenPopupId) {
        openHavenSpacePopup(havenPopupId, { buildingId: resolvedId, hotspotId: idCandidate });
        return;
      }

      logEvent("building_entered", { building_id: resolvedId });
      enterRoom(locationFromBuildingId(districts, resolvedId, {}));
      armBuildingInspectionSession(4000);
      const session = {
        buildingId: resolvedId,
        buildingName: record?.building?.name ?? null,
        mode: "inspection",
      };
      buildingSessionRef.current = session;
      patchUnityInspection(session);

      [0, 300, 1000].forEach((ms) => {
        returnToInspectionTimersRef.current.push(
          setTimeout(() => resyncBuildingRoomUnlocks(resolvedId), ms)
        );
      });
    };

    const handleRoomEntered = ({ buildingID, roomID }) => {
      const districts = worldRef.current.districts;
      const record = findBuildingRecord(districts, buildingID, {
        roomId: roomID,
        preferLocationHint: false,
      });
      const location = locationFromRoomId(districts, roomID);
      const resolvedId = record?.building?.id ?? (buildingID || location.buildingId);
      console.warn('[RoomEntered] roomID:', roomID, '| districtCount:', districts?.length ?? 0, '| resolved:', !!location.districtId, '| location:', location);
      const sinceLastRoomEntered = Date.now() - lastRoomEnteredAtRef.current;
      const isDuplicateRoomEntered = roomID === lastRoomEnteredIdRef.current && sinceLastRoomEntered < 2000;
      if (!isDuplicateRoomEntered) {
        lastRoomEnteredIdRef.current = roomID;
        lastRoomEnteredAtRef.current = Date.now();
        logEvent("room_entered", { building_id: resolvedId, room_id: roomID });
      }
      if (location.districtId) {
      
        pendingUnityRoomIdRef.current = null;
        enterRoom(location);
      } else if (roomID) {
        
        pendingUnityRoomIdRef.current = roomID;
        if (location.roomId) enterRoom(location);
      }
      // Re-entering the room whose conversation is already loaded: enterRoom() is a no-op for
      // currentLocation.roomId, so the load effect correctly skips a redundant fetch — but that
      // also means nothing re-opens the panel after a door exit closed it. Re-assert it here so
      // auto-open does not depend on whether the location happened to change in between.
      if (roomID && roomID === lumenActiveRoomIdRef.current && lumenMessagesRef.current.length > 0) {
        requestLumenOpen();
      }
      doorExitHandledUntilRef.current = 0;
      doorClickArmedRef.current = false;
      buildingExitedPendingRef.current = false;
      inUnityRoomRef.current = true;
      const session = {
        buildingId: resolvedId,
        buildingName: record?.building?.name ?? null,
        mode: "firstperson",
      };
      buildingSessionRef.current = session;
      patchUnityInspection(session);
    };

    const armBuildingInspectionSession = (ms = 4000) => {
      suppressFullBuildingExitUntilRef.current = Date.now() + ms;
    };

    const clearReturnToInspectionTimers = () => {
      returnToInspectionTimersRef.current.forEach((id) => clearTimeout(id));
      returnToInspectionTimersRef.current = [];
    };

    const isLeavingRoom = () => {
      if (doorClickArmedRef.current || buildingExitedPendingRef.current) return true;
      const inspection = unityInspectionRef.current;
      if (!inspection) return Boolean(buildingSessionRef.current);
      if (inspection.mode === "firstperson") return true;
      if (inUnityRoomRef.current) return true;
      return Boolean(currentLocationRef.current.roomId);
    };

    const resyncBuildingRoomUnlocks = (buildingId) => {
      const record = findBuildingRecord(worldRef.current.districts, buildingId, {
        locationBuildingId: buildingId,
      });
      const cmds = getUnityBridgeCommands();
      if (!record || !cmds) return;
      if (record.building.is_unlocked !== false) {
        cmds.setBuildingUnlockState(record.building.id, true);
      }
      for (const floor of record.building.floors || []) {
        for (const room of floor.rooms || []) {
          if (room.is_unlocked) {
            cmds.setRoomUnlockState(room.id, true);
          }
        }
      }
    };

    const syncReactToBuildingInspection = () => {
      const inspection = unityInspectionRef.current;
      inUnityRoomRef.current = false;
      patchUnityInspection((prev) => (prev ? { ...prev, mode: "inspection" } : null));
      if (inspection?.buildingId) {
        const buildingId = inspection.buildingId;
        returnToInspectionTimersRef.current.push(
          setTimeout(() => {
            enterRoom(
              locationFromBuildingId(worldRef.current.districts, buildingId, {
                locationBuildingId: buildingId,
              })
            );
          }, 400)
        );
      }
    };

    const completeDoorExitToInspection = () => {
      const inspection = unityInspectionRef.current ?? buildingSessionRef.current;
      if (!inspection) return;
      if (!unityInspectionRef.current) {
        patchUnityInspection({ ...inspection, mode: "inspection" });
      }

      if (!isLeavingRoom()) return;

      const now = Date.now();
      armBuildingInspectionSession(4000);
      /* Do not call ReturnToInspection — Unity already transitions on door click. */

      if (now < doorExitHandledUntilRef.current) return;
      doorExitHandledUntilRef.current = now + 900;

      setLumenOpen(false);
      setLumenDrafting(false);
      syncReactToBuildingInspection();

      const buildingId = unityInspectionRef.current?.buildingId ?? inspection.buildingId;
      if (buildingId) {
        buildingSessionRef.current = {
          buildingId,
          buildingName: unityInspectionRef.current?.buildingName ?? inspection.buildingName,
          mode: "inspection",
        };
        returnToInspectionTimersRef.current.push(
          setTimeout(() => resyncBuildingRoomUnlocks(buildingId), 300)
        );
      }
    };

    const armDoorClick = () => {
      doorClickArmedRef.current = true;
      returnToInspectionTimersRef.current.push(
        setTimeout(() => {
          doorClickArmedRef.current = false;
        }, 2500)
      );
    };

    const handleDoorClicked = () => {
      const inspection = unityInspectionRef.current ?? buildingSessionRef.current;
      if (!inspection) return;
      armDoorClick();
      // React state sync happens in handleBuildingExited when Unity confirms the exit.
    };

    // Door click: Unity has already moved to aerial view — just sync the UI.
    const handleBuildingExited = (payload) => {
      const exitedId = payload?.buildingID ?? payload?.buildingId ?? null;
      if (exitedId && !isUnityPlaceholderBuildingId(exitedId)) lastVisitedBuildingIdRef.current = exitedId;

      if (explicitMapExitRef.current) {
        explicitMapExitRef.current = false;
      }

      if (
        !isLeavingRoom() &&
        Date.now() < ignoreBuildingExitUntilRef.current &&
        unityInspectionRef.current?.mode !== "inspection" &&
        unityInspectionRef.current?.mode !== "firstperson"
      ) {
        console.timeLog("[BuildingExited] ignored — stale exit window");
        return;
      }

      logEvent("building_exited", {});
      clearBuildingSessionForMap();
    };

    const handleDoorNavigation = () => {
      handleDoorClicked();
    };

    const handleNavigationHotspot = (hotspotId) => {
      const ref = String(hotspotId).toLowerCase();
      if (ref.endsWith(":door") || ref === "door") {
        handleDoorNavigation();
      }
    };

    const openRestoredHotspot = (hotspotId) => {
      if (!hotspotId) return false;
      let item = findSurfaceItemById(hotspotId);
      let resolvedId = hotspotId;
      if (!item && !hotspotId.includes(":") && currentLocationRef.current?.roomId) {
        resolvedId = `${currentLocationRef.current.roomId}:${hotspotId}`;
        item = findSurfaceItemById(resolvedId);
      }
      const panelId = hotspotIdFromUnityRef(resolvedId, findSurfaceItemById);
      if ((item && isDeskHotspotSurface(item.surface)) || panelId === "desk") {
        unityHotspotRef.current = { id: hotspotId, panel: "desk", roomId: item?.room_id || currentLocationRef.current?.roomId || null };
        if (item) hotspotPopupHandlersRef.current.openDeskPanel?.(item);
        else setActiveHotspot("desk");
        return true;
      }
      if (!panelId) return false;
      unityHotspotRef.current = { id: hotspotId, panel: panelId, roomId: item?.room_id || currentLocationRef.current?.roomId || null };
      setActiveHotspot(panelId);
      return true;
    };
    restoreHotspotRef.current = openRestoredHotspot;

    sessionRestoredSyncRef.current = ({ mode, roomID, buildingID, hotspotID, fallback }) => {
      if (mode === "FirstPersonView" && roomID) {
        const alreadyInRoom = currentLocationRef.current?.roomId === roomID;
        pendingRestoreHotspotRef.current = hotspotID && !fallback && !alreadyInRoom ? { roomId: roomID, hotspotId: hotspotID } : null;
        handleRoomEntered({ buildingID, roomID });
        if (hotspotID && !fallback && alreadyInRoom) openRestoredHotspot(hotspotID);
        return;
      }
      // Aerial — or the saved room is gone/locked (fallback). Drop any building/room UI.
      const loc = currentLocationRef.current;
      if (fallback || loc.roomId || loc.buildingId || unityInspectionRef.current) {
        clearBuildingSessionForMap();
      }
    };

    inspectionTapProbeRef.current = () => {
      if (unityInspectionRef.current?.mode !== "inspection") return;
      if (inUnityRoomRef.current) return;
      logEvent("building_exited", { via: "tap_to_aerial" });
      clearBuildingSessionForMap();
    };

    setUnityHotspotClickHandler(handleHotspotClick);
    setUnityNavigationHotspotHandler(handleNavigationHotspot);
    setUnityBuildingEnteredHandler(handleBuildingEntered);
    setUnityRoomEnteredHandler(handleRoomEntered);
    setUnityBuildingExitedHandler(handleBuildingExited);
    setUnityDoorClickedHandler(handleDoorClicked);
    setUnityHotspotClosedHandler(() => {
      unityHotspotRef.current = null;
      setActiveHotspot(null);
    });

    return () => {
      clearReturnToInspectionTimers();
      clearTimeout(aerialProbeTimerRef.current);
      inspectionTapProbeRef.current = null;
      sessionRestoredSyncRef.current = null;
      restoreHotspotRef.current = null;
      setUnityHotspotClickHandler(null);
      setUnityNavigationHotspotHandler(null);
      setUnityBuildingEnteredHandler(null);
      setUnityRoomEnteredHandler(null);
      setUnityBuildingExitedHandler(null);
      setUnityDoorClickedHandler(null);
      setUnityHotspotClosedHandler(null);
    };
  }, [
    findSurfaceItemById,
    hydrateDeskFromSurfaceItem,
    enterRoomForSurfaceItem,
    enterRoom,
    logEvent,
  ]);

  // World JSON can load after Unity fires BuildingEntered — re-resolve placeholder id/name.
  useEffect(() => {
    const inspection = unityInspectionRef.current;
    if (!inspection) return;

    const record = resolveInspectionBuilding(world.districts, inspection, currentLocation);
    const name = record?.building?.name;
    const id = record?.building?.id;
    if (!name || !id) return;

    if (inspection.buildingId === id && inspection.buildingName === name) return;

    patchUnityInspection((prev) =>
      prev ? { ...prev, buildingId: id, buildingName: name } : null
    );
  }, [world.districts, currentLocation.buildingId, currentLocation.roomId]);

 
  useEffect(() => {
    const pendingId = pendingUnityRoomIdRef.current;
    if (!pendingId || !world.districts?.length) return;
    const location = locationFromRoomId(world.districts, pendingId);
    if (!location.districtId) return; // still not found
    pendingUnityRoomIdRef.current = null;
    enterRoom(location);
  }, [world.districts, enterRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const suppressBuildingTeardownRef = useRef(false);

  // Clear building session only when user closes the Unity panel (not when desk opens).
  useEffect(() => {
    if (activeHotspot === null) {
      if (suppressBuildingTeardownRef.current) {
        suppressBuildingTeardownRef.current = false;
        return;
      }
      returnToInspectionTimersRef.current.forEach((id) => clearTimeout(id));
      returnToInspectionTimersRef.current = [];
      buildingSessionRef.current = null;
      patchUnityInspection(null);
    }
  }, [activeHotspot]);

  // ── Shared timer state (survives dock/undock) ──────────────────
  const [pomPhase,  setPomPhase]  = useState("work");
  const [pomSecs,   setPomSecs]   = useState(25 * 60);
  const [pomRun,    setPomRun]    = useState(false);
  const handleSetPomRun = (val) => {
    const next = typeof val === "function" ? val(pomRun) : val;
    if (next && !pomRun) logEvent("timer_start", { room_id: currentLocation.roomId, phase: pomPhase });
    setPomRun(next);
  };
  const [pomPreset, setPomPreset] = useState({ work: 25, rest: 5 });

  useEffect(() => {
    if (!pomRun) return;
    const workS = pomPreset.work * 60, restS = pomPreset.rest * 60;
    const t = setInterval(() => setPomSecs(s => {
      if (s <= 1) {
        clearInterval(t);
        if (pomPhase === "work") { setPomPhase("rest"); setPomSecs(restS); }
        else { setPomPhase("work"); setPomSecs(workS); }
        return 0;
      }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [pomRun, pomPhase, pomPreset]);
  const [myDayOpen, setMyDayOpen]           = useState(false);
  const [lumenOpen, setLumenOpen]           = useState(false);
  const [lumenMaximized, setLumenMaximized] = useState(false);
  const [lumenSplit, setLumenSplit] = useState(DEFAULT_LUMEN_FRAC);
  const [seamDragging, setSeamDragging] = useState(false);
  const [, setViewportTick] = useState(0);
  const seamDragRef = useRef(null);
  // lumenLoadingRef blocks the write effect during room-switch loads to prevent overwriting
  // freshly loaded data with stale state.
  const [lumenMessages, setLumenMessages]           = useState([]);
  const [lumenExchangeCount, setLumenExchangeCount] = useState(0);
  /** True while autoDraftDeskDocument is running after room welcome — drives Lumen ··· loader. */
  const [lumenDrafting, setLumenDrafting]           = useState(false);
  /** room_id whose restored thread should show the "welcome back" prompt + buttons, else null. */
  const [lumenWelcomeBack, setLumenWelcomeBack]     = useState(null);
  const lumenPersistTimerRef  = useRef(null);
  const lumenLoadingRef       = useRef(false);
  /** Monotonic id of the newest conversation-load run — only that run may release the lock. */
  const lumenLoadRunRef       = useRef(0);
  const lumenSyncRef          = useRef(null);
  const [lumenSessions, setLumenSessions]           = useState([]);
  const [lumenActiveSession, setLumenActiveSession] = useState(null);
  const lumenSessionsRef      = useRef([]);
  lumenSessionsRef.current = lumenSessions;
  const [lumenLinks, setLumenLinks]                 = useState([]);
  const [lumenLeftOff, setLumenLeftOff]             = useState(null);
  const [lumenBuildingSessions, setLumenBuildingSessions] = useState([]);
  const pendingOpenSessionRef = useRef(null);
  /** Mirror of previousSession — the load callback resolves after enterRoom mutates it. */
  const previousSessionRef    = useRef(null);
  previousSessionRef.current  = previousSession;
  /** Tracks which room_id Lumen is currently loaded for — prevents wipe on inspection re-entry. */
  const lumenActiveRoomIdRef  = useRef(null);
  /** Mirror of lumenMessages as a ref — lets the autoDraft finally flush without stale closure. */
  const lumenMessagesRef      = useRef([]);
  lumenMessagesRef.current = lumenMessages;

  const {
    pendingDeskAction, focusDeskDoc, setFocusDeskDoc,
    handleDeskAction, resolvePendingDeskAction, closePendingDeskAction, undoDeskReplace, deskChoices,
  } = useDeskActions({ setActiveHotspot, setLumenMessages });

  // Inject brain-dump room greeting into shared lumenMessages and immediately clear roomGreeting
  // so it cannot re-fire on any LumenCard remount (maximize, minimize, etc.).
  useEffect(() => {
    if (!roomGreeting) return;
    if (lumenMessages.length === 0) {
      const count = roomGreeting.itemCount;
      setLumenMessages([{
        role: "assistant",
        content: `I've added ${count} item${count !== 1 ? "s" : ""} from your brain dump to the bulletin board in this room. Want to start there and work through them now?`,
      }]);
    }
    setRoomGreeting(null);
  }, [roomGreeting]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lumenLoadingRef.current) return; // blocked during room-switch load
    const sync = lumenSyncRef.current;
    if (!user?.id || !sync) return;
    const writeRoomId = currentLocation?.roomId || '__haven__';
    clearTimeout(lumenPersistTimerRef.current);
    lumenPersistTimerRef.current = setTimeout(async () => {
      const liveRoomId = currentLocation.roomId || '__haven__';
      if (liveRoomId !== writeRoomId) {
        console.warn('[Lumen] write skipped — room changed between schedule and fire. scheduled:', writeRoomId, 'live:', liveRoomId);
        return;
      }
      const { error } = await sync.save(lumenMessages, { surface: activeHotspotRef.current || null, hotspotId: currentUnityHotspotId() });
      if (error) console.error('[Lumen] persist error:', error.message);
      else autoTitleLumenSession(sync, lumenMessages);
    }, 300);
    return () => clearTimeout(lumenPersistTimerRef.current);
  }, [lumenMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoTitleLumenSession = (sync, messages) => {
    sync.autoTitle(messages, (msgs) => generateLumenSessionTitle(msgs, { roomName: getCurrentRoom()?.room?.name }))
      .then((result) => {
        if (result?.error) console.error('[Lumen] title error:', result.error.message);
        if (!result?.ok || lumenSyncRef.current !== sync) return;
        setLumenSessions((prev) => prev.map((row) => (row.id === result.sessionId ? { ...row, title: result.title } : row)));
      });
  };

  const currentUnityHotspotId = () => {
    const open = unityHotspotRef.current;
    if (!open || !activeHotspotRef.current || open.panel !== activeHotspotRef.current) return null;
    return open.roomId && open.roomId !== currentLocationRef.current?.roomId ? null : open.id;
  };

  const flushLumenSave = (sync) => {
    clearTimeout(lumenPersistTimerRef.current);
    if (lumenLoadingRef.current) return;
    const msgs = lumenMessagesRef.current;
    if (!sync.sessionId && !msgs.some((m) => m.role === 'user')) return;
    sync.save(msgs, { surface: activeHotspotRef.current || null, hotspotId: currentUnityHotspotId() }).then(({ error }) => {
      if (error) console.error('[Lumen] persist error:', error.message);
    });
  };

  const lockLumenWrites = () => {
    const runToken = ++lumenLoadRunRef.current;
    lumenLoadingRef.current = true;
    setLumenWelcomeBack(null);
    return {
      current: () => runToken === lumenLoadRunRef.current,
      release: () => setTimeout(() => {
        if (runToken === lumenLoadRunRef.current) lumenLoadingRef.current = false;
      }, 200),
    };
  };

  const newLumenSessionGreeting = () => {
    const name = getCurrentRoom()?.room?.name;
    return {
      role: 'assistant',
      content: name
        ? `New session in ${name}. I've got everything from this room, so we can start anywhere.`
        : "New session. I've got everything from your world, so we can start anywhere.",
      isWelcome: true,
      isNewSession: true,
    };
  };

  const showNewLumenSession = (sync, lock) => {
    sync.startNew();
    setLumenExchangeCount(0);
    setLumenMessages([newLumenSessionGreeting()]);
    lock.release();
  };

  const openLumenSession = async (sync, sessionId, lock) => {
    const { messages, error } = await sync.open(sessionId);
    if (!lock.current() || lumenSyncRef.current !== sync) return;
    if (error) {
      console.error('[Lumen] open session error:', error.message);
    } else {
      setLumenMessages(settleStaleDeskReceipts(messages).map((m) => ({ ...m, isRestored: true })));
      setLumenExchangeCount(messages.filter((m) => m.role === 'user').length);
      autoTitleLumenSession(sync, messages);
    }
    lock.release();
  };

  const handleLumenStartFresh = () => {
    const sync = lumenSyncRef.current;
    if (!sync || lumenLoadingRef.current) return;
    flushLumenSave(sync);
    showNewLumenSession(sync, lockLumenWrites());
  };

  const handleLumenOpenSession = (sessionId) => {
    const sync = lumenSyncRef.current;
    if (!sync || !sessionId || sessionId === sync.sessionId || lumenLoadingRef.current) return;
    flushLumenSave(sync);
    openLumenSession(sync, sessionId, lockLumenWrites());
  };

  const handleLumenUsage = (usage) => {
    const sync = lumenSyncRef.current;
    if (!sync) return;
    sync.recordUsage(usage).then((result) => {
      if (result?.error) console.error('[Lumen] usage error:', result.error.message);
    });
  };

  const handleLumenContinueSession = () => {
    const sync = lumenSyncRef.current;
    if (!sync || lumenLoadingRef.current) return;
    const previous = sync.session;
    const carried = carryMessages(lumenMessagesRef.current);
    flushLumenSave(sync);
    const lock = lockLumenWrites();
    sync.startNew({ relatedTo: previous?.id || null, title: continuedTitle(previous?.title) });
    setLumenExchangeCount(carried.filter((m) => m.role === 'user').length);
    setLumenMessages([
      {
        role: 'assistant',
        content: previous?.title
          ? `Continuing from “${previous.title}”. Here's where we left it:`
          : "Continuing from your last chat. Here's where we left it:",
        isWelcome: true,
      },
      ...carried,
    ]);
    lock.release();
  };

  const handleLumenSetRelated = async (sessionId, relatedIds) => {
    if (!user?.id) return;
    const result = await setRelatedSessions({ userId: user.id, sessionId, relatedIds, links: lumenLinks });
    if (result.error) {
      console.error('[Lumen] related chats error:', result.error.message);
      return;
    }
    setLumenLinks(result.links);
  };

  const handleLumenLeftOffPickUp = () => {
    const held = heldRestoreHotspotRef.current;
    const lateHeld = held && held.roomId === lumenLeftOff?.roomId ? held : null;
    if (lateHeld) heldRestoreHotspotRef.current = null;
    const leftOff = lumenLeftOff && !lumenLeftOff.restoredHotspot && lateHeld ? { ...lumenLeftOff, restoredHotspot: lateHeld } : lumenLeftOff;
    console.info("[Lumen] Pick up", { restored: leftOff?.restoredHotspot?.hotspotId || null, chatHotspot: leftOff?.hotspotId || null, surface: leftOff?.surface || null });
    setLumenLeftOff(null);
    setLumenMaximized(false);
    const openAndGo = (hotspotId, needsGo) => {
      if (!hotspotId || !restoreHotspotRef.current?.(hotspotId)) return false;
      if (needsGo) getUnityBridgeCommands()?.goToHotspot?.(hotspotId);
      return true;
    };
    const opened = openAndGo(leftOff?.restoredHotspot?.hotspotId, leftOff?.restoredHotspot?.needsGo)
      || openAndGo(leftOff?.hotspotId, true)
      || (!!leftOff?.surface && !!leftOff.roomId && leftOff.roomId !== '__haven__' && openAndGo(`${leftOff.roomId}:${leftOff.surface}`, true));
    if (!opened && leftOff?.surface) setActiveHotspot(leftOff.surface);
    setLumenOpen(true);
  };

  const dismissLumenLeftOff = () => {
    if (heldRestoreHotspotRef.current?.roomId === lumenLeftOff?.roomId) heldRestoreHotspotRef.current = null;
    if (lumenLeftOff?.restoredHotspot && !lumenLeftOff.restoredHotspot.needsGo) getUnityBridgeCommands()?.closeHotspot?.();
    setLumenLeftOff(null);
  };

  const handleLumenLeftOffNewSession = () => {
    dismissLumenLeftOff();
    setLumenMaximized(false);
    setLumenOpen(true);
    handleLumenStartFresh();
  };

  const handleLumenRenameSession = async (sessionId, title) => {
    const sync = lumenSyncRef.current;
    if (!sync) return;
    const result = await sync.rename(sessionId, title);
    if (result.error) {
      console.error('[Lumen] rename error:', result.error.message);
      return;
    }
    if (lumenSyncRef.current !== sync) return;
    setLumenSessions((prev) => prev.map((row) => (row.id === sessionId ? { ...row, title: result.title } : row)));
  };

  const handleLumenDeleteSession = async (sessionId) => {
    const sync = lumenSyncRef.current;
    if (!sync || lumenLoadingRef.current) return;
    const wasActive = sync.sessionId === sessionId;
    if (wasActive) clearTimeout(lumenPersistTimerRef.current);
    const lock = wasActive ? lockLumenWrites() : null;
    const result = await sync.remove(sessionId);
    if (result.error) {
      console.error('[Lumen] delete error:', result.error.message);
      lock?.release();
      return;
    }
    if (lumenSyncRef.current !== sync) return;
    const remaining = lumenSessionsRef.current.filter((row) => row.id !== sessionId);
    setLumenSessions(remaining);
    if (!wasActive) return;
    if (remaining.length > 0) openLumenSession(sync, remaining[0].id, lock);
    else showNewLumenSession(sync, lock);
  };

  const [couchPip, setCouchPip]             = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileResetting, setProfileResetting] = useState(false);
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  const [audioChannels, setAudioChannels] = useState(AUDIO_DEFAULT_STATE);
  
  // const profileMenuRef = useRef(null);
  const [havenSpace, setHavenSpace]           = useState(null);
  const [hotspotPopupId, setHotspotPopupId]     = useState(null);
  const pendingHotspotRef = useRef(null);
  const hotspotPopupHandlersRef = useRef({});
  const [lumenFocusMode, setLumenFocusMode]   = useState(false);
  // Panel bounds - null means use defaults
  const [panelBounds, setPanelBounds] = useState(null); // { top, left, right, bottom }
  const [whiteboardBounds, setWhiteboardBounds] = useState(null);

  useEffect(() => {
    if (activeHotspot !== "whiteboard") {
      setWhiteboardBounds(null);
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWhiteboardBounds(whiteboardFrameBounds()));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(frame);
    };
  }, [activeHotspot]);

  useEffect(() => {
    setLumenSplit(loadLumenSplit(user?.id, activeHotspot || FREE_DOCK_KEY));
  }, [activeHotspot, user?.id]);

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setViewportTick((n) => n + 1));
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); cancelAnimationFrame(frame); };
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const drag = seamDragRef.current;
      if (!drag || !drag.avail) return;
      const height = drag.startH + (drag.startY - e.clientY);
      if (height <= DOCK_COLLAPSED_H + COLLAPSE_SNAP) {
        drag.collapsed = true;
        setLumenOpen(false);
        return;
      }
      drag.collapsed = false;
      drag.frac = clampLumenFrac(height / drag.avail);
      setLumenSplit(drag.frac);
      setLumenOpen(true);
    };
    const onUp = () => {
      const drag = seamDragRef.current;
      if (!drag) return;
      seamDragRef.current = null;
      setSeamDragging(false);
      if (!drag.collapsed && drag.frac) saveLumenSplit(user?.id, drag.hotspot, drag.frac);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [user?.id]);

  // ── Panel resize drag ──────────────────────────────────────────
  const panelDragging  = useRef(null); // "left"|"right"|"top"|"bottom"|null
  const panelDragStart = useRef({ x: 0, y: 0 });
  const panelBoundsStart = useRef(null);

  const handleAudioChannelChange = useCallback((key, patch) => {
    setAudioChannels((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const allAudioDisabled = useMemo(() => {
   return (
     (audioChannels.master.muted || audioChannels.master.volume === 0) &&
     (audioChannels.music.muted || audioChannels.music.volume === 0) &&
     (audioChannels.sfx.muted || audioChannels.sfx.volume === 0)
   );
  }, [audioChannels]);
  
  useEffect(() => {
    setAllMuted(allAudioDisabled);
  }, [allAudioDisabled]);


  useEffect(() => {
    const onMove = (e) => {
      if (!panelDragging.current) return;
      const dx = e.clientX - panelDragStart.current.x;
      const dy = e.clientY - panelDragStart.current.y;
      const b  = { ...panelBoundsStart.current };
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (panelDragging.current === "left")   b.left   = Math.max(0,  Math.min(vw - 320, b.left   + dx));
      if (panelDragging.current === "right")  b.right  = Math.max(0,  Math.min(vw - 320, b.right  - dx));
      if (panelDragging.current === "top")    b.top    = Math.max(58, Math.min(vh - 120, b.top    + dy));
      if (panelDragging.current === "bottom") b.bottom = Math.max(16, Math.min(vh - 120, b.bottom - dy));
      setPanelBounds({ ...b });
    };
    const onUp = () => { panelDragging.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const AVATAR_SKIN_COLORS = ["#FDDBB4","#F0C68A","#D4965A","#A0634A","#6B3A2A","#3D1F10"];
  const menuItemStyle = { display:"block", width:"100%", padding:"10px 16px", background:"none", border:"none", textAlign:"left", fontSize:13, fontWeight:500, color:"#1E293B", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" };

  const handleSignOut = () => {
    setProfileMenuOpen(false);
    signOut();
  };

  // New user / incomplete Brain Dump: Lumen in front after Unity loads until work world exists.
  useEffect(() => {
    if (!user?.id) {
      brainDumpOnboardingShownRef.current = false;
      return;
    }
    if (!worldReady || !needsBrainDump || !unityLoaded) return;
    if (brainDumpOnboardingShownRef.current) return;
    brainDumpOnboardingShownRef.current = true;
    setBrainDumpOpen(false);
    setLumenOpen(false);
    setLumenMaximized(true);
  }, [user?.id, worldReady, needsBrainDump, unityLoaded]);

  useEffect(() => {
    if (!user?.id) {
      unityKeepMountedRef.current = false;
      setUnityLoaded(false);
    }
  }, [user?.id]);

  const stackVisible = myDayOpen;
  const PANEL_W      = 320;
  const COLLAPSED_TAB_W = 34;
  const TOP_NAV_HEIGHT = 48;
  // Mount Unity for new users (needsBrainDump, empty work districts) — Haven lives in the baked scene.
  const unityWorldReady = Boolean(
    user?.id && worldReady && (world?.districts?.length > 0 || needsBrainDump || worldLoadFailed)
  );
  if (unityWorldReady) unityKeepMountedRef.current = true;
  const showUnityWorld = unityKeepMountedRef.current;
  const hasBuiltWorkWorld = Boolean(worldReady && !needsBrainDump && world?.districts?.length > 0);
  const unityInBuilding = Boolean(unityInspection);
  const unityFullscreen = showUnityWorld && !lumenMaximized;
  const unityLayerRaised = unityFullscreen || unityInBuilding;
  const hotspotRight = stackVisible ? PANEL_W + 16 : 50;
  const _defaultBounds = activeHotspot === "whiteboard"
    ? (whiteboardBounds || whiteboardFrameBounds())
    : { top: 74, bottom: 30, left: Math.round(window.innerWidth * 0.21), right: Math.round(window.innerWidth * 0.21) };
  const panelBase = panelBounds || _defaultBounds;
  const dockGeo = dockGeometry({
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    topInset: TOP_NAV_HEIGHT,
    // The bookshelf is a full-room overlay with no panel box, so Lumen stays free-docked under it.
    hotspot: activeHotspot && activeHotspot !== "bookshelf" && !lumenMaximized && unityLoaded ? panelBase : null,
    open: lumenOpen,
    frac: lumenSplit,
  });
  const activeBounds = dockGeo.hotspotBottom != null ? { ...panelBase, bottom: dockGeo.hotspotBottom } : panelBase;
  const dockChips = activeHotspot === "whiteboard" ? hotspotChips : (HOTSPOT_PROMPT_CHIPS[activeHotspot] || null);
  const dockSubtitle = activeHotspot ? (HOTSPOT_LABELS[activeHotspot] || activeHotspot) : null;

  const floorBarAnchor = {
    top: Math.max(TOP_NAV_HEIGHT + 15 + 46 + 12, Math.round(window.innerHeight * 0.2)),
  };

  const onSeamDown = (e) => {
    e.preventDefault();
    seamDragRef.current = { startY: e.clientY, startH: dockGeo.dock.height, avail: dockGeo.avail, hotspot: activeHotspot || FREE_DOCK_KEY };
    setSeamDragging(true);
  };
  const startDrag = (edge, e) => {
    panelDragging.current  = edge;
    panelDragStart.current = { x: e.clientX, y: e.clientY };
    panelBoundsStart.current = panelBounds || _defaultBounds;
    e.preventDefault();
  };

  const currentRoomCtx = getCurrentRoom();
  const whiteboardHasImage = (roomSurfaceItems[currentLocation.roomId] || [])
    .some((item) => item.surface === "whiteboard" && item.image_path);

  const handleWhiteboardSnapshot = async (roomId, content) => {
    if (!roomId) return;
    getUnityBridgeCommands()?.setWhiteboardImage?.(roomId, content || "");
    if (!user?.id) return;
    const previous = (persistedSurfaceItemsRef.current || [])
      .find((item) => item.room_id === roomId && item.surface === "whiteboard" && item.image_path)?.image_path || null;
    try {
      if (content) {
        const path = await uploadWhiteboardSnapshot(user.id, roomId, content);
        setWhiteboardImage(roomId, { path, updatedAt: Date.now() });
      } else {
        setWhiteboardImage(roomId, null);
      }
      if (previous) {
        setTimeout(() => {
          deleteRoomFile(previous).catch((err) => console.warn("[whiteboard] could not remove the old board image:", err?.message || err));
        }, 10000);
      }
    } catch (err) {
      console.warn("[whiteboard] could not save the board image:", err?.message || err);
    }
  };
  const currentBuilding = currentRoomCtx?.building || null;
  const buildingRoomIds = (currentBuilding?.floors || [])
    .flatMap((floor) => (floor.rooms || []).map((room) => room.id))
    .filter(Boolean);
  const buildingRoomKey = buildingRoomIds.join(",");

  const inBuildingView = !currentLocation.roomId && !!currentLocation.buildingId;
  const viewedBuilding = useMemo(
    () => (inBuildingView ? findBuildingRecord(world?.districts, currentLocation.buildingId)?.building || null : null),
    [inBuildingView, world, currentLocation.buildingId]
  );
  const viewedBuildingRooms = useMemo(
    () => (viewedBuilding?.floors || []).flatMap((floor) => (floor.rooms || []).map((room) => ({ id: room.id, name: room.name || "Room" }))),
    [viewedBuilding]
  );
  const viewedBuildingRoomKey = viewedBuildingRooms.map((room) => room.id).join(",");

  useEffect(() => {
    if (!user?.id || !viewedBuildingRoomKey) {
      setLumenBuildingSessions([]);
      return undefined;
    }
    let cancelled = false;
    listBuildingSessions({ userId: user.id, roomIds: viewedBuildingRoomKey.split(",") }).then(({ sessions, error }) => {
      if (cancelled) return;
      if (error) console.error('[Lumen] building sessions error:', error.message);
      setLumenBuildingSessions(sessions);
    });
    return () => { cancelled = true; };
  }, [user?.id, viewedBuildingRoomKey]);

  const lumenSessionIdsKey = lumenSessions.map((row) => row.id).sort().join(",");
  useEffect(() => {
    if (!user?.id || !lumenSessionIdsKey) {
      setLumenLinks([]);
      return undefined;
    }
    let cancelled = false;
    listSessionLinks({ userId: user.id, sessionIds: lumenSessionIdsKey.split(",") }).then(({ links, error }) => {
      if (cancelled) return;
      if (error) console.error('[Lumen] related chats error:', error.message);
      else setLumenLinks(links);
    });
    return () => { cancelled = true; };
  }, [user?.id, lumenSessionIdsKey]);

  const handleLumenOpenBuildingSession = (sessionId) => {
    const row = lumenBuildingSessions.find((item) => item.id === sessionId);
    if (!row) return;
    const location = locationFromRoomId(worldRef.current?.districts, row.room_id);
    if (!location?.roomId) return;
    if (row.room_id === lumenActiveRoomIdRef.current && lumenSyncRef.current) {
      handleLumenOpenSession(sessionId);
    } else {
      pendingOpenSessionRef.current = { roomId: row.room_id, sessionId };
    }
    enterUnityRoomFirstPerson(location, { buildingName: viewedBuilding?.name ?? null });
    setLumenOpen(true);
  };

  useEffect(() => {
    if (!user?.id || !buildingRoomKey) {
      setBuildingActivity([]);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from("room_activity")
      .select("room_id, last_summary, next_step, updated_at")
      .eq("user_id", user.id)
      .in("room_id", buildingRoomKey.split(","))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.warn("[lumen] could not read building activity:", error.message);
        setBuildingActivity(data || []);
      });
    return () => { cancelled = true; };
  }, [user?.id, buildingRoomKey, currentLocation.roomId, roomSummaryVersion]);

  const projectOverview = useMemo(() => describeProject({
    building: currentBuilding,
    currentRoomId: currentLocation.roomId,
    roomSurfaceItems,
    activities: buildingActivity,
  }), [currentBuilding, currentLocation.roomId, roomSurfaceItems, buildingActivity]);
  const roomName   = currentRoomCtx?.room?.name || "My Room";
  const showRoomLocationChrome = Boolean(currentRoomCtx && unityInspection?.mode === "firstperson" && worldRevealed);
  const breadcrumb = showRoomLocationChrome
    ? `${currentRoomCtx.island.name} › ${currentRoomCtx.building.name} › ${currentRoomCtx.room.name}`
    : "";
  const activeBlock = activeBlockId ? dayBlocks.find(b => b.id === activeBlockId) : null;

  // Everything Lumen writes through here is answering something the user asked for in this room,
  // so it counts as work started — unlike the desk auto-populate that runs on first entry.
  const handleSurfaceUpdate = (update) => addSurfaceItem({ ...update, room_id: currentLocation.roomId, user_edited: true });

  const handleNavigateToLastRoom = async () => {
    const roomId = lastRoomIdFromSession(previousSession);
    if (!roomId) return;

    const location = locationFromRoomId(world.districts, roomId);
    if (!location.roomId) {
      console.warn("[NeuroQuest] Pick up where I left off: room not in world", roomId);
      return;
    }

    logEvent("pick_up_last_room", {
      room_id: roomId,
      room_name: previousSession?.last_activity?.room_name || roomNameFromLocation(world.districts, location) || null,
    });

    setLumenMaximized(false);
    setLumenOpen(false);

    const unityResult = await enterUnityRoomFirstPerson(location);
    if (!unityResult.ok && process.env.NODE_ENV === "development") {
      console.warn("[NeuroQuest → Unity] GoToRoom failed", unityResult.error);
    }
  };

  const safeSurface = surfaceState || { whiteboardCards: [], bulletinItems: [], calendarEvents: [], deskDocuments: [], bookshelfDocs: [] };

  const lumenLastWorkedOn = useMemo(() => {
    if (currentLocation.roomId) {
      return (surfaceState?.deskDocuments || []).find((d) => d.user_edited && (d.title || '').trim())?.title || null;
    }
    const lastRoomId = lastRoomIdFromSession(previousSession);
    if (!lastRoomId) return null;
    const deskItem = (roomSurfaceItems[lastRoomId] || []).find(
      (item) => isDeskHotspotSurface(item.surface) && item.user_edited && (item.title || '').trim()
    );
    return deskItem?.title || null;
  }, [currentLocation.roomId, surfaceState, previousSession, roomSurfaceItems]);

  const openDeskPanel = (surfaceItem) => {
    if (surfaceItem) {
      logEvent("hotspot_clicked", {
        surface_item_id: surfaceItem.id,
        room_id: surfaceItem.room_id,
        surface: surfaceItem.surface,
      });
      enterRoomForSurfaceItem(surfaceItem);
      hydrateDesk(surfaceItem);
      setLumenMaximized(false);
      setLumenOpen(false);
    } else {
      setLumenMaximized(false);
      setLumenOpen(true);
    }
    setHotspotPopupId(null);
    pendingHotspotRef.current = null;
    setActiveHotspot("desk");
  };

  const requestHotspotPopup = (id, { surfaceItem } = {}) => {
    const plan = planHotspotOpen(id, { userId: user?.id, hasActiveHotspot: activeHotspot !== null });
    if (!plan.known) return false;
    if (isHavenSpacePopup(id)) setWorldGuideOpen(false);
    setHotspotPopupId(null);
    pendingHotspotRef.current = null;

    if (plan.openDesk) {
      openDeskPanel(surfaceItem);
      return true;
    }
    if (plan.clearActiveHotspot) {
      suppressBuildingTeardownRef.current = true;
      setActiveHotspot(null);
    }
    if (plan.openPanel) {
      setActiveHotspot(plan.openPanel);
      return true;
    }
    pendingHotspotRef.current = { id, surfaceItem };
    setHotspotPopupId(id);
    return true;
  };

  const closeHotspotPopup = () => {
    const pending = pendingHotspotRef.current;
    setHotspotPopupId(null);
    pendingHotspotRef.current = null;
    if (!pending) return;

    const plan = planHotspotClose(pending.id, user?.id);
    if (plan.openDesk) openDeskPanel(pending.surfaceItem);
    else if (plan.openPanel) setActiveHotspot(plan.openPanel);
  };

  const hotspotUiOpen = !!(hotspotPopupId || activeHotspot);
  const hotspotUiOpenRef = useRef(false);
  useEffect(() => {
    if (hotspotUiOpenRef.current && !hotspotUiOpen) {
      getUnityBridgeCommands()?.closeHotspot?.();
    }
    hotspotUiOpenRef.current = hotspotUiOpen;
  }, [hotspotUiOpen]);

  hotspotPopupHandlersRef.current = { openDeskPanel, requestHotspotPopup };
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    window.__nqShowPopup = (id) => requestHotspotPopup(id);
    // Dev-only: most hotspots (bookshelf, whiteboard, couch, painting...) only show a
    // "coming soon" info popup right now — clicking them in Unity never opens the real
    // panel. Use this in the console to open one directly while testing locally, e.g.
    // __nqOpenHotspot("bookshelf")
    window.__nqOpenHotspot = (id) => setActiveHotspot(id);
  }

  const [fidgetOpen, setFidgetOpen] = useState(false);

  // ── Theme state — driven by PaintingPanel ──────────────────────
  const THEMES = {
    deep_focus:    { wall: "#2A2A35", floor: "#3A2A1A", accent: "#C8A040", light: "#FFB060", dark: true },
    golden_hour:   { wall: "#F5EDD8", floor: "#A8784A", accent: "#C8A040", light: "#FFD060", dark: false },
    creative_flow: { wall: "#F0EEEA", floor: "#D4C4A8", accent: "#E84878", light: "#FFF5E0", dark: false },
    calm:          { wall: "#B8C9A8", floor: "#6B8B6A", accent: "#4DB8B5", light: "#E0F8F0", dark: false },
    midnight:      { wall: "#1A1A2E", floor: "#111118", accent: "#4DB8B5", light: "#40F0C0", dark: true },
    fresh_start:   { wall: "#F8F8F6", floor: "#E8E4DC", accent: "#1A1A1A", light: "#C0DCFF", dark: false },
  };
  const [activeThemeId, setActiveThemeId] = useState("golden_hour");
  const activeTheme = THEMES[activeThemeId] || THEMES.golden_hour;

  return (
    <>
    
    {isUserTnCNotAccepted() && <LegalAgreementModal userProfile={userProfile} onSubmit={(updatedProfile)=>{setUserProfile(updatedProfile)}} userId={user.id} />}
    <DialogHost />
    <div style={{ width: "100vw", height: "100vh", fontFamily: "'DM Sans', sans-serif", overflow: "hidden", position: "relative", background: showUnityWorld ? "#0F172A" : activeTheme.wall, transition: "background 0.6s ease",pointerEvents: isUserTnCNotAccepted() ? "none" :"initial" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      {/* ── Room theme fallback when Unity world is not loaded yet ── */}
      {!showUnityWorld && (
        <>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "30%", background: `radial-gradient(ellipse at 50% 0%, ${activeTheme.light}44 0%, transparent 70%)`, pointerEvents: "none", transition: "background 0.6s ease", zIndex: 0 }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "22%", background: `linear-gradient(180deg, ${activeTheme.wall} 0%, ${activeTheme.floor} 100%)`, pointerEvents: "none", transition: "background 0.6s ease", zIndex: 0 }} />
          <div style={{ position: "absolute", bottom: "22%", left: 0, right: 0, height: 3, background: activeTheme.accent, opacity: 0.5, pointerEvents: "none", transition: "background 0.6s ease", zIndex: 0 }} />
        </>
      )}

      {showRoomLocationChrome && activeHotspot !== "whiteboard" && (
        <div
          style={{ position: "absolute", top: 58 + 12, left: 60, zIndex: 48, pointerEvents: isUserTnCNotAccepted() ? "none" : "all", cursor: "pointer", textAlign: "left" }}
        >
          <div style={{ background: activeTheme.dark ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", borderRadius: 12, padding: "8px 14px", border: `1px solid ${activeTheme.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", transition: "box-shadow 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.15)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"}
          >
            <div style={{ fontSize: 9, fontWeight: 700, color: activeTheme.dark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
              {currentRoomCtx.island.name} › {currentRoomCtx.building.name}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: activeTheme.dark ? "rgba(255,255,255,0.85)" : "#1E293B", letterSpacing: "0.01em", lineHeight: 1 }}>
              {currentRoomCtx.room.name}
            </div>
            <div style={{ fontSize: 9, color: activeTheme.dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.25)", marginTop: 3, letterSpacing: "0.06em" }}>
              {currentRoomCtx.floor.name}
            </div>
          </div>
        </div>
      )}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", background: "rgba(0,0,0,0.25)", backdropFilter: "blur(8px)", zIndex: 50 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>🌍 {world.name}{breadcrumb ? ` › ${breadcrumb}` : ""}</div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <DockedTimerPill
            pomRun={pomRun} setPomRun={handleSetPomRun}
            pomSecs={pomSecs} phase={pomPhase}
            onDetach={() => setTimerDocked(false)}
            onExpand={() => { setTimerDocked(false); setTimerExpanded(true); }}
          />
          <button onClick={() => setFidgetOpen(o => !o)} style={{ padding: "6px 14px", borderRadius: 8, background: fidgetOpen ? "rgba(77,184,181,0.2)" : "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🫧 Fidget</button>
          <button onClick={() => hasBuiltWorkWorld ? setMapOpen(true) : setBrainDumpOpen(true)} style={{ padding: "6px 14px", borderRadius: 8, background: hasBuiltWorkWorld ? "#4DB8B5" : "rgba(255,255,255,0.12)", border: "none", color: hasBuiltWorkWorld ? "#0F172A" : "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>🗺 Map</button>
          <button onClick={() => setAudioSettingsOpen(o => !o)} style={{ padding: "6px 14px", borderRadius: 8, background: audioSettingsOpen ? "rgba(77,184,181,0.2)" : "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🔊 Audio</button>

          {/* Profile avatar button */}
          <div  style={{ position: "relative" }}>
            <button onClick={() => setProfileMenuOpen(o => !o)}
              style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${profileMenuOpen ? "#4DB8B5" : "rgba(255,255,255,0.3)"}`, background: userProfile?.avatar?.skin != null ? AVATAR_SKIN_COLORS[userProfile.avatar.skin] : "rgba(255,255,255,0.15)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#1E293B", transition: "border-color 0.15s", flexShrink: 0, fontFamily: "'DM Sans',sans-serif" }}>
              {(appUser?.name || userProfile?.avatar?.name)?.[0]?.toUpperCase() || "👤"}
            </button>
            {profileMenuOpen && createPortal(
              <>
              <div
                onClick={() => setProfileMenuOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                }}
              />
              <div 
                role="menu"
                style={{ position: "fixed", top: 48, right: 16, background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.22)", minWidth: 210, zIndex: 100000, overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)" }}
              >
                <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  {(appUser?.name || userProfile?.avatar?.name) && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", marginBottom: 2 }}>
                      {appUser?.name || userProfile.avatar.name}
                    </div>
                  )}
                  {user?.email && <div style={{ fontSize: 11, color: "#94A3B8", wordBreak: "break-all" }}>{user.email}</div>}
                  {!userProfile?.avatar?.name && !user?.email && <div style={{ fontSize: 12, color: "#94A3B8" }}>No profile yet</div>}
                </div>
                {/* <button type="button" onClick={() => { setProfileMenuOpen(false); setActiveHotspot("painting"); }} style={menuItemStyle}
                  onMouseEnter={e => e.currentTarget.style.background="#F8FAFC"} onMouseLeave={e => e.currentTarget.style.background="none"}>
                  🖼 Change Room Theme
                </button> */}
                <button
                  type="button"
                  disabled={profileResetting}
                  onClick={async () => {
                    const confirmed = await confirmDialog({
                      title: "Reset your world?",
                      message: "Delete your world and all saved room data?\n\nYour account stays signed in. For testing only — this cannot be undone.",
                      confirmLabel: "Delete",
                      danger: true,
                    });
                    if (!confirmed) return;

                    setProfileResetting(true);
                    try {
                      const result = await resetToNewUser();
                      if (!result?.ok) {
                        await alertDialog({
                          title: "Reset failed",
                          message: `${result?.error || "Unknown error"}.`,
                        });
                        return;
                      }

                      brainDumpOnboardingShownRef.current = false;
                      setBrainDumpOpen(false);
                      setMapOpen(false);
                      setWorldGuideOpen(false);
                      patchUnityInspection(null);
                      buildingSessionRef.current = null;
                      inUnityRoomRef.current = false;
                      setLumenOpen(false);
                      setLumenMaximized(true);
                    } finally {
                      setProfileResetting(false);
                      setProfileMenuOpen(false);
                    }
                  }}
                  style={{
                    ...menuItemStyle,
                    opacity: profileResetting ? 0.75 : 1,
                    cursor: profileResetting ? "progress" : "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (profileResetting) return;
                    e.currentTarget.style.background = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "none";
                  }}
                >
                  {profileResetting ? "⏳ Resetting..." : "🔄 Reset Profile"}
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSignOut();
                  }}
                  style={{ ...menuItemStyle, color: "#EF4444", borderTop: "1px solid rgba(0,0,0,0.06)" }}
                  onMouseEnter={e => e.currentTarget.style.background="#FFF5F5"} onMouseLeave={e => e.currentTarget.style.background="none"}>
                  Sign Out
                </button>
              </div></>,
              document.body
            )}
          </div>
        </div>
      </div>

      {/* World Guide — always available over Unity */}
      <>
      {/* onClick={() => setWorldGuideOpen(o => !o)} */}
      <div  style={{ display:"none",position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", background: worldGuideOpen ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.85)", padding: "14px 8px", borderRadius: "0 10px 10px 0", zIndex: 55, cursor: "pointer", borderLeft: `3px solid ${worldGuideOpen ? "#4DB8B5" : "transparent"}`, transition: "all 0.15s", userSelect: "none" }}>
        <div style={{ writingMode: "vertical-rl", fontSize: 10, fontWeight: 700, color: worldGuideOpen ? "#4DB8B5" : "#64748B", letterSpacing: "0.04em" }}>🌍 World Guide</div>
      </div>
      <WorldGuide isOpen={worldGuideOpen} onClose={() => setWorldGuideOpen(false)} currentRoom={currentLocation.roomId || "nq-f1-desk"}
        onNavigate={(room) => {
          if (room.isHaven) {
            requestHotspotPopup(room.hotspot || room.id);
            setWorldGuideOpen(false);
          } else {
            // Find full location for this room and navigate
            for (const district of world.districts) {
              for (const island of district.islands || []) {
                for (const building of island.buildings || []) {
                  for (const floor of building.floors || []) {
                    const found = (floor.rooms || []).find(r => r.id === room.id);
                    if (found) {
                      enterRoom({ districtId: district.id, islandId: island.id, buildingId: building.id, floorId: floor.id, roomId: room.id });
                      if (room.hotspot) requestHotspotPopup(room.hotspot);
                      setWorldGuideOpen(false);
                      return;
                    }
                  }
                }
              }
            }
            setWorldGuideOpen(false);
          }
        }}
        onBrainDump={() => { setWorldGuideOpen(false); setBrainDumpOpen(true); }} />
      </>

      {/* Unity 3D world — loads on page open when world exists; fullscreen when Unity hotspot selected */}
      {showUnityWorld && (
        <div
          // Clicking or dragging in the world with no hotspot open means the user is moving around,
          // so Lumen folds back to its strip and stops covering the view.
          onPointerDownCapture={(e) => {
            if (!activeHotspot && !lumenMaximized) setLumenOpen(false);
            unityTapStartRef.current = { x: e.clientX, y: e.clientY };
          }}
          onPointerUpCapture={(e) => {
            const start = unityTapStartRef.current;
            unityTapStartRef.current = null;
            if (!start || unityInspectionRef.current?.mode !== "inspection") return;
            if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > UNITY_TAP_SLOP_PX) return;
            clearTimeout(aerialProbeTimerRef.current);
            aerialProbeTimerRef.current = setTimeout(() => inspectionTapProbeRef.current?.(), 700);
          }}
          style={{
            position: "fixed",
            top: TOP_NAV_HEIGHT,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: unityLayerRaised ? 25 : 5,
            background: "#0F172A",
            transition: "right 0.28s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <div style={{ position: "relative", width: "100%", height: "100%" }}>
            <UnityMessagePanel fullscreen onClose={() => setActiveHotspot(null)} onLoadChange={setUnityLoaded} holdReveal={!unityPlacementReady} />
          </div>
        </div>
      )}

      {showUnityWorld && unityInBuilding && (
        <UnityInspectionBar
          anchor={floorBarAnchor}
          inspection={unityInspection}
          floorCount={
            unityInspection
              ? resolveInspectionBuilding(world.districts, unityInspection, currentLocation)
                  ?.building?.floors?.length || 1
              : 1
          }
          onBackToBuilding={() => {
            const now = Date.now();
            doorExitHandledUntilRef.current = now + 900;
            suppressFullBuildingExitUntilRef.current = now + 4000;
            inUnityRoomRef.current = false;
            patchUnityInspection((prev) => (prev ? { ...prev, mode: "inspection" } : null));
          }}
          onExitToMap={() => {
            const leavingId =
              unityInspectionRef.current?.buildingId ?? buildingSessionRef.current?.buildingId;
            if (leavingId) lastVisitedBuildingIdRef.current = leavingId;
            lastConcreteBuildingEnteredAtRef.current = 0;
            lastConcreteBuildingIdRef.current = null;
            explicitMapExitRef.current = true;
            buildingSessionRef.current = null;
            suppressFullBuildingExitUntilRef.current = 0;
            inUnityRoomRef.current = false;
            patchUnityInspection(null);
            enterRoom(locationFromBuildingId(world.districts, null, {}));
            returnToInspectionTimersRef.current.forEach((id) => clearTimeout(id));
            returnToInspectionTimersRef.current = [];
          }}
        />
      )}

      {((activeHotspot && activeHotspot !== "bookshelf") || lumenMaximized) && (
        <>
          {/* 4-edge resize handles — only when lumenMaximized */}
          {lumenMaximized && unityLoaded && (
            <>
              {/* Left */}
              <div onMouseDown={e => startDrag("left", e)}
                style={{ position: "fixed", top: activeBounds.top, bottom: activeBounds.bottom, left: activeBounds.left - 3, width: 7, zIndex: 50, cursor: "ew-resize", background: "transparent" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(77,184,181,0.3)"}
                onMouseLeave={e => { if (!panelDragging.current) e.currentTarget.style.background = "transparent"; }} />
              {/* Right */}
              <div onMouseDown={e => startDrag("right", e)}
                style={{ position: "fixed", top: activeBounds.top, bottom: activeBounds.bottom, right: activeBounds.right - 3, width: 7, zIndex: 50, cursor: "ew-resize", background: "transparent" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(77,184,181,0.3)"}
                onMouseLeave={e => { if (!panelDragging.current) e.currentTarget.style.background = "transparent"; }} />
              {/* Top */}
              <div onMouseDown={e => startDrag("top", e)}
                style={{ position: "fixed", left: activeBounds.left, right: activeBounds.right, top: activeBounds.top - 3, height: 7, zIndex: 50, cursor: "ns-resize", background: "transparent" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(77,184,181,0.3)"}
                onMouseLeave={e => { if (!panelDragging.current) e.currentTarget.style.background = "transparent"; }} />
              {/* Bottom */}
              <div onMouseDown={e => startDrag("bottom", e)}
                style={{ position: "fixed", left: activeBounds.left, right: activeBounds.right, bottom: activeBounds.bottom - 3, height: 7, zIndex: 50, cursor: "ns-resize", background: "transparent" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(77,184,181,0.3)"}
                onMouseLeave={e => { if (!panelDragging.current) e.currentTarget.style.background = "transparent"; }} />
            </>
          )}
          {unityLoaded && <div style={{ position: "fixed", top: activeBounds.top, left: activeBounds.left, right: activeBounds.right, bottom: activeBounds.bottom, background: "rgba(255,255,255,0.97)", borderRadius: 16, zIndex: 45, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
          {lumenMaximized && (
            <LumenCard
              roomName={roomName}
              activeSurface={activeHotspot}
              whiteboardContents={whiteboardContents}
              projectOverview={projectOverview}
              buildingName={currentRoomCtx?.building?.name || "My Building"}
              islandName={currentRoomCtx?.island?.name || "My Island"}
              floorName={currentRoomCtx?.floor?.name || "My Floor"}
              surfaceState={safeSurface}
              onSurfaceUpdate={handleSurfaceUpdate} onDeskAction={handleDeskAction}
              deskChoices={deskChoices}
              height="100%" minimized={false}
              onToggleMinimize={() => { setLumenMaximized(false); setLumenOpen(true); setPanelBounds(null); }}
              isMaximized
              onUnmaximize={() => { setLumenMaximized(false); setLumenOpen(true); setRoomGreeting(null); setPanelBounds(null); }}
              onClose={() => {
                setLumenMaximized(false);
                setLumenOpen(false);
                setRoomGreeting(null);
                setPanelBounds(null);
              }}
              onBrainDump={() => setBrainDumpOpen(true)}
              currentMode={activeHotspot === "couch" ? "couch" : brainDumpOpen ? "brain_dump" : "work"}
              userProfile={userProfile}
              logEvent={logEvent}
              focusMode={lumenFocusMode}
              onFocusMode={() => setLumenFocusMode(true)}
              onExitFocusMode={() => setLumenFocusMode(false)}
              hasWorld={hasBuiltWorkWorld}
              previousSession={previousSession}
              currentRoomActivity={currentRoomActivity}
              justBuiltWorld={justBuiltWorld}
              onJustBuiltWorldDismiss={() => setJustBuiltWorld(false)}
              onNavigateToLastRoom={handleNavigateToLastRoom}
              onOpenMap={() => setMapOpen(true)}
              onOpenDesk={() => setActiveHotspot("desk")}
              messages={lumenMessages}
              setMessages={setLumenMessages}
              exchangeCount={lumenExchangeCount}
              setExchangeCount={setLumenExchangeCount}
              onStartFresh={handleLumenStartFresh}
              onUsage={handleLumenUsage}
              capState={capState(lumenActiveSession?.token_count, lumenActiveSession?.status)}
              onContinueSession={handleLumenContinueSession}
              onParseWorld={handleParseWorld}
              onSaveWorld={handleSaveWorld}
              world={world}
              externalThinking={lumenDrafting}
              welcomeBack={!!lumenWelcomeBack}
              lastWorkedOn={lumenLastWorkedOn}
              visitedRoomThisSession={visitedRoomThisSession}
              onWelcomeBackDismiss={() => setLumenWelcomeBack(null)}
            />
          )}
          {!lumenMaximized && activeHotspot === "desk"       && (
            <DeskPanel
              key={currentLocation.roomId || "no-room"}
              onClose={async () => {
                await flushWorldJson();
                setActiveHotspot(null);
                closePendingDeskAction();
                setFocusDeskDoc(null);
              }}
              surfaceState={safeSurface}
              onSurfaceUpdate={handleSurfaceUpdate}
              onDeskDocumentChange={updateDeskDocument}
              onDeleteDocument={deleteDeskDocument}
              focusDeskDoc={focusDeskDoc}
              pendingDeskAction={pendingDeskAction}
              onResolvePendingDeskAction={resolvePendingDeskAction}
              onUndoDeskReplace={undoDeskReplace}
              registerDeskFlush={registerDeskFlush}
              worldSaving={worldSaving}
              worldLastSavedAt={worldLastSavedAt}
              worldAutosavePending={worldAutosavePending}
              worldSaveError={worldSaveError}
              onAutosaveUiReset={resetDeskAutosaveUi}
              roomCtx={currentRoomCtx}
              roomId={currentLocation.roomId}
              focusDocTitle={deskFocusTitle}
              onFocusHandled={() => setDeskFocusTitle(null)}
              existingDocTitles={(safeSurface.deskDocuments || []).map(d => d.title).filter(Boolean)}
            />
          )}
          {!lumenMaximized && activeHotspot === "whiteboard" && <WhiteboardPanel onClose={() => setActiveHotspot(null)} onSnapshot={handleWhiteboardSnapshot} hasImage={whiteboardHasImage} onContentsChange={setWhiteboardContents} onStartsChange={setHotspotChips} externalCards={safeSurface.whiteboardCards} userId={user?.id} roomId={currentLocation.roomId} roomContext={{ room: currentRoomCtx?.room?.name, building: currentRoomCtx?.building?.name, island: currentRoomCtx?.island?.name }} onAskLumen={() => { setLumenOpen(true); setLumenWelcomeBack(null); setLumenMessages(prev => [...prev, { role: "assistant", content: "What are you working on at this whiteboard? Tell me the gist and I'll suggest a structure that fits this room." }]); }} onSendToDesk={(item) => { const title = item?.title || String(item?.content || "Whiteboard").substring(0, 40); const body = item?.image ? `<p><img src="${item.image}" alt="Whiteboard export" /></p>` : `<p>${item?.content || ""}</p>`; addSurfaceItem({ surface: "desk", type: "document", title, content: body, forceNew: true, user_edited: true }); setActiveHotspot("desk"); }} onSendToBookshelf={(item) => addSurfaceItem({ surface: "bookshelf", type: "reference", source_hotspot: "whiteboard", title: item?.title || String(item?.content || "Whiteboard").substring(0, 40), content: item?.image || item?.content || "" })} />}
          {!lumenMaximized && activeHotspot === "calendar"       && <CalendarPanel onClose={() => setActiveHotspot(null)} />}
          {!lumenMaximized && activeHotspot === "bulletin_board" && <BulletinPanel onClose={() => setActiveHotspot(null)} />}
          {!lumenMaximized && activeHotspot === "couch"      && <CouchPanel onClose={() => setActiveHotspot(null)} onMinimize={(label) => { setCouchPip({ label }); setActiveHotspot(null); }} />}
          {!lumenMaximized && activeHotspot === "painting"   && <PaintingPanel onClose={() => setActiveHotspot(null)} onThemeChange={(id) => setActiveThemeId(id)} activeThemeId={activeThemeId} />}
        </div>
        }
        </>
      )}

      {unityLoaded && !lumenMaximized && activeHotspot === "bookshelf" && (
        <BookshelfPanel
          onClose={() => setActiveHotspot(null)}
          rightInset={stackVisible ? PANEL_W : COLLAPSED_TAB_W}
          bottomInset={lumenFocusMode ? 0 : dockGeo.dock.bottom + dockGeo.dock.height + SEAM_H}
          deskDocIds={(safeSurface.deskDocuments || [])
            .filter(d => {
              const docRoom = d.roomId || d.room_id;
              return !docRoom || !currentLocation.roomId || docRoom === currentLocation.roomId;
            })
            .map(d => d.id)
            .filter(Boolean)}
          roomId={currentLocation.roomId}
          userId={user?.id}
          onDeskDocDeleted={deleteDeskDocument}
          onOpenInHotspot={(target, doc) => {
            if (target === "desk") {
              const deskDocId = deskDocIdForBook(doc.id);
              const alreadyOpen = safeSurface.deskDocuments?.some(
                d => d.id === deskDocId || d.title === doc.title
              );
              setDeskFocusTitle(doc.title || null);
              if (alreadyOpen) {
                setActiveHotspot("desk");
                return;
              }
              addSurfaceItem({
                id: deskDocId,
                surface: "desk",
                type: "document",
                title: doc.title,
                content: typeof doc.content === "string" ? doc.content : `<p>${doc.title}</p>`,
                room_id: currentLocation.roomId,
                user_edited: true,
              });
              setActiveHotspot("desk");
            } else if (target === "whiteboard") {
              addSurfaceItem({
                surface: "whiteboard",
                type: "idea",
                content: typeof doc.content === "string"
                  ? doc.content.replace(/<[^>]+>/g, " ").trim().substring(0, 300)
                  : doc.title,
                title: doc.title,
              });
              setActiveHotspot("whiteboard");
            }
          }}
        />
      )}

      {/* Fidget widget - floating, not a hotspot panel */}
      <FidgetWidget isOpen={fidgetOpen} onClose={() => setFidgetOpen(false)} />

      {worldRevealed && lumenLeftOff && lumenLeftOff.roomId === currentLocation.roomId && (
        <LumenLeftOffPopup
          surface={lumenLeftOff.surface}
          title={lumenLeftOff.title}
          roomName={roomName}
          summary={currentRoomActivity?.room_id === lumenLeftOff.roomId ? currentRoomActivity.last_summary : null}
          nextStep={currentRoomActivity?.room_id === lumenLeftOff.roomId ? currentRoomActivity.next_step : null}
          onPickUp={handleLumenLeftOffPickUp}
          onNewSession={handleLumenLeftOffNewSession}
          onClose={dismissLumenLeftOff}
        />
      )}
      {worldRevealed && !lumenMaximized && !lumenFocusMode && (
        <>
          {dockGeo.seam && (
            <div
              data-lumen-seam
              onMouseDown={onSeamDown}
              title="Drag to resize"
              style={{
                position: "fixed", left: dockGeo.seam.left, right: dockGeo.seam.right, bottom: dockGeo.seam.bottom,
                height: dockGeo.seam.height, zIndex: 56, cursor: "row-resize", display: "flex",
                alignItems: "center", justifyContent: "center",
                pointerEvents: isUserTnCNotAccepted() ? "none" : "auto",
              }}
            >
              <div style={{
                width: 56, height: 14, borderRadius: 999, background: "white", display: "flex",
                alignItems: "center", justifyContent: "center", gap: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)", border: "1px solid rgba(0,0,0,0.06)",
              }}>
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} style={{ width: 2, height: 7, borderRadius: 1, background: seamDragging ? "#4DB8B5" : "#64748B" }} />
                ))}
              </div>
            </div>
          )}
          <div
            data-lumen-dock-frame
            style={{
              position: "fixed", left: dockGeo.dock.left, right: dockGeo.dock.right, bottom: dockGeo.dock.bottom,
              height: dockGeo.dock.height, zIndex: 55,
              transition: seamDragging ? "none" : "height 0.22s ease, left 0.22s ease, right 0.22s ease",
              pointerEvents: isUserTnCNotAccepted() ? "none" : "auto",
            }}
          >
            <LumenCard
              dock={{
                collapsed: !lumenOpen,
                onExpand: () => setLumenOpen(true),
                onCollapse: () => setLumenOpen(false),
                subtitle: inBuildingView ? (viewedBuilding?.name || dockSubtitle) : dockSubtitle,
                chips: dockChips,
                sessions: !user?.id ? null : inBuildingView ? {
                  mode: "building",
                  items: lumenBuildingSessions,
                  rooms: viewedBuildingRooms,
                  activeId: lumenActiveSession?.id || null,
                  onOpen: handleLumenOpenBuildingSession,
                } : {
                  items: lumenSessions,
                  activeId: lumenActiveSession?.id || null,
                  onOpen: handleLumenOpenSession,
                  onNew: handleLumenStartFresh,
                  onRename: handleLumenRenameSession,
                  onDelete: handleLumenDeleteSession,
                  relatedOf: (id) => relatedIdsFor(lumenLinks, id),
                  onSetRelated: handleLumenSetRelated,
                },
              }}
              roomName={roomName}
              buildingName={currentRoomCtx?.building?.name || "My Building"}
              islandName={currentRoomCtx?.island?.name || "My Island"}
              floorName={currentRoomCtx?.floor?.name || "My Floor"}
              activeSurface={activeHotspot}
              whiteboardContents={whiteboardContents}
              projectOverview={projectOverview}
              surfaceState={safeSurface}
              onSurfaceUpdate={handleSurfaceUpdate}
              onDeskAction={handleDeskAction}
              deskChoices={deskChoices}
              minimized={false}
              onMaximize={unityLoaded ? () => { setLumenMaximized(true); setLumenOpen(false); } : undefined}
              onBrainDump={() => setBrainDumpOpen(true)}
              currentMode={activeHotspot === "couch" ? "couch" : brainDumpOpen ? "brain_dump" : "work"}
              userProfile={userProfile}
              logEvent={logEvent}
              hasWorld={hasBuiltWorkWorld}
              previousSession={previousSession}
              currentRoomActivity={currentRoomActivity}
              justBuiltWorld={justBuiltWorld}
              onJustBuiltWorldDismiss={() => setJustBuiltWorld(false)}
              onNavigateToLastRoom={handleNavigateToLastRoom}
              onOpenMap={() => setMapOpen(true)}
              onOpenDesk={() => setActiveHotspot("desk")}
              messages={lumenMessages}
              setMessages={setLumenMessages}
              exchangeCount={lumenExchangeCount}
              setExchangeCount={setLumenExchangeCount}
              onStartFresh={handleLumenStartFresh}
              onUsage={handleLumenUsage}
              capState={capState(lumenActiveSession?.token_count, lumenActiveSession?.status)}
              onContinueSession={handleLumenContinueSession}
              onParseWorld={handleParseWorld}
              onSaveWorld={handleSaveWorld}
              world={world}
              externalThinking={lumenDrafting}
              welcomeBack={!!lumenWelcomeBack}
              lastWorkedOn={lumenLastWorkedOn}
              visitedRoomThisSession={visitedRoomThisSession}
              onWelcomeBackDismiss={() => setLumenWelcomeBack(null)}
            />
          </div>
        </>
      )}
      {worldRevealed && <RightStack
        roomName={roomName}
        activeSurface={activeHotspot}
        whiteboardContents={whiteboardContents}
        projectOverview={projectOverview}
        buildingName={currentRoomCtx?.building?.name || "My Building"}
        islandName={currentRoomCtx?.island?.name || "My Island"}
        floorName={currentRoomCtx?.floor?.name || "My Floor"}
        surfaceState={safeSurface}
        onSurfaceUpdate={handleSurfaceUpdate} onDeskAction={handleDeskAction}
        activeBlockId={activeBlockId} onActivateBlock={setActiveBlockId}
        blocks={dayBlocks}
        myDayOpen={myDayOpen}
        setMyDayOpen={setMyDayOpen}
        lumenOpen={lumenOpen} setLumenOpen={setLumenOpen}
        lumenMaximized={lumenMaximized}
        onLumenMaximize={unityLoaded ? () => { setLumenMaximized(true); setLumenOpen(false); } : undefined}
        onBrainDump={() => setBrainDumpOpen(true)}
        currentMode={activeHotspot === "couch" ? "couch" : brainDumpOpen ? "brain_dump" : "work"}
        userProfile={userProfile}
        logEvent={logEvent}
        hasWorld={hasBuiltWorkWorld}
        previousSession={previousSession}
        currentRoomActivity={currentRoomActivity}
        justBuiltWorld={justBuiltWorld}
        onJustBuiltWorldDismiss={() => setJustBuiltWorld(false)}
        onNavigateToLastRoom={handleNavigateToLastRoom}
        onOpenMap={() => setMapOpen(true)}
        onOpenDesk={() => setActiveHotspot("desk")}
        lumenMessages={lumenMessages}
        setLumenMessages={setLumenMessages}
        lumenExchangeCount={lumenExchangeCount}
        setLumenExchangeCount={setLumenExchangeCount}
        lumenOnStartFresh={handleLumenStartFresh}
        onParseWorld={handleParseWorld}
        onSaveWorld={handleSaveWorld}
        world={world}
        isUserTnCNotAccepted={isUserTnCNotAccepted}
        lumenDrafting={lumenDrafting}
        lumenWelcomeBack={!!lumenWelcomeBack}
        lumenLastWorkedOn={lumenLastWorkedOn}
        lumenVisitedRoomThisSession={visitedRoomThisSession}
        onLumenWelcomeBackDismiss={() => setLumenWelcomeBack(null)}
      />}

      {/* Floating timer */}
      <FloatingTimer
        activeBlock={activeBlock}
        docked={timerDocked}
        startExpanded={timerExpanded}
        onDock={() => { setTimerDocked(true); setTimerExpanded(false); }}
        sharedTimer={{ pomRun, setPomRun: handleSetPomRun, pomSecs, setPomSecs, pomPhase, setPomPhase, pomPreset, setPomPreset }}
      />

      {brainDumpOpen && <BrainDumpModal onClose={() => setBrainDumpOpen(false)} />}

      {/* Focus mode overlay - dark fullscreen, rendered outside clipped panel */}
      {lumenFocusMode && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(10,14,20,0.96)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}
          onKeyDown={e => e.key === "Escape" && setLumenFocusMode(false)}
          tabIndex={-1}
        >
          <FocusModeChat
            userProfile={userProfile}
            activeSurface={activeHotspot}
            whiteboardContents={whiteboardContents}
            projectOverview={projectOverview}
            surfaceState={safeSurface}
            onSurfaceUpdate={handleSurfaceUpdate}
            onDeskAction={handleDeskAction}
            deskChoices={deskChoices}
            roomName={roomName}
            buildingName={currentRoomCtx?.building?.name || "My Building"}
            islandName={currentRoomCtx?.island?.name || "My Island"}
            floorName={currentRoomCtx?.floor?.name || "My Floor"}
            currentRoomActivity={currentRoomActivity}
            currentMode="focus"
            onExit={() => setLumenFocusMode(false)}
            onBrainDump={() => { setLumenFocusMode(false); setBrainDumpOpen(true); }}
          />
        </div>
      )}
      {havenSpace && <HavenPanel space={havenSpace} onClose={() => setHavenSpace(null)} onBrainDump={() => { setHavenSpace(null); setBrainDumpOpen(true); }} onSpaceSelect={(id) => requestHotspotPopup(id)} />}
      {hotspotPopupId && <HotspotPopup hotspotId={hotspotPopupId} onClose={closeHotspotPopup} />}
      {hotspotUiOpen && (
        <HotspotNavArrows
          topOffset={TOP_NAV_HEIGHT}
          zIndex={hotspotPopupId ? 10000 : 54}
          hidden={lumenFocusMode || lumenMaximized}
        />
      )}
      {audioSettingsOpen && (
        <AudioSettingsPanel
          onClose={() => setAudioSettingsOpen(false)}
          channels={audioChannels}
          onChannelChange={handleAudioChannelChange}
        />
      )}
      {couchPip && (
        <CouchPiP
          label={couchPip.label}
          onExpand={() => { setCouchPip(null); setActiveHotspot("couch"); }}
          onClose={() => setCouchPip(null)}
        />
      )}
      {mapOpen && (
        <MapView
          onClose={() => setMapOpen(false)}
          onPositionChange={updateMapPosition}
          onEnterRoom={(room, floor, building, island) => {
            const location = {
              districtId: island.district.id,
              islandId: island.id,
              buildingId: building.id,
              floorId: floor.id,
              roomId: room.id,
            };
            enterUnityRoomFirstPerson(location, { buildingName: building.name ?? null });
            setMapOpen(false);
          }}
        />
      )}
    </div>
    </>
  );
}

function AppGate() {
  const { authReady, sessionBootstrapping, authCallbackError, user } = useSpatial();
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const syncPath = () => setPath(window.location.pathname);
    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, []);

  useEffect(() => {
    if (!authReady || sessionBootstrapping) return;

    if (user && isAccessPath(path)) {
      window.history.replaceState({}, "", "/");
      setPath("/");
      return;
    }
    const hasAuthCallback = /[?#].*(code=|access_token=|error=)/i.test(window.location.href);
    if (!user && !isAccessPath(path) && !hasAuthCallback) {
      window.history.replaceState({}, "", ACCESS_PATH);
      setPath(ACCESS_PATH);
    }
  }, [authReady, sessionBootstrapping, user, path]);

  const gate = (bootstrapping) => (
    <MagicLinkGate bootstrapping={bootstrapping} authCallbackError={authCallbackError} />
  );

  if (!authReady || sessionBootstrapping) {
    return gate(true);
  }

  if (!user) {
    if (!isAccessPath(path)) return null;
    return gate(false);
  }

  if (!isAccessPath(path) && path !== "/") {
    window.history.replaceState({}, "", "/");
    return <InnerApp />;
  }

  return <InnerApp />;
}

export default function App() {
  return (
    <AudioProvider> 
      <BookshelfProvider>
        <SpatialProvider>
          <AppGate />
        </SpatialProvider>
      </BookshelfProvider>
    </AudioProvider>
  );
}
